import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Logo from '../components/Logo';
import LandingBlogSection from '../components/LandingBlogSection';
import HeroTechVisual from '../components/HeroTechVisual';
import { FileText, PencilSimpleLine, DeviceMobile, ShieldCheck, Clock, CurrencyEur, ArrowRight, Check, Globe, Receipt, Printer, Warning, Envelope, MapPin, IdentificationCard, ChartBar, Calculator, Star, CaretRight, Scales, UserCircle, House, ArrowDown, Archive, Calendar, Bell, ChartLine, Fire, Gift, Buildings, Sparkle, TrendUp, WhatsappLogo, Camera, ArrowsClockwise, Bed, QrCode, WifiHigh as Wifi, Lightning } from '@phosphor-icons/react';import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '../components/ui/accordion';

const IMG2 = 'https://images.unsplash.com/photo-1723124125642-d414ed7683e2?w=800&q=80&auto=format';
const VIDEO_BG = 'https://videos.pexels.com/video-files/2869738/2869738-sd_640_360_30fps.mp4';

const faqData = [
  { q: "Il contratto di locazione turistica è obbligatorio?", a: "Sì, il contratto di locazione turistica è obbligatorio per legge in Italia per tutti gli affitti brevi con durata inferiore a 30 giorni (art. 4 DL 50/2017). Deve essere stipulato in forma scritta. La mancata stipula può comportare sanzioni amministrative. Con Locazione Turistica lo crei in pochi secondi e lo fai firmare online." },
  { q: "La ricevuta di pagamento è obbligatoria?", a: "No, con il contratto di locazione turistica la ricevuta non è obbligatoria per legge, in quanto l'host privato non esercita attività commerciale (art. 4 D.L. 50/2017). Tuttavia, ad espressa richiesta dell'ospite, siamo tenuti a rilasciarla. Se l'importo supera €77,47 è obbligatorio apporre la marca da bollo da €2,00 (DPR 642/1972)." },
  { q: "Quando serve la marca da bollo sulla ricevuta?", a: "Ai sensi del DPR 642/1972, quando l'importo supera €77,47 è obbligatorio apporre una marca da bollo da €2,00. La marca deve avere data uguale o antecedente alla ricevuta. Mai postdatata! Può comportare sanzioni in caso di controlli." },
  { q: "Perché conviene fare la ricevuta per prenotazioni extra piattaforma?", a: "Senza la ritenuta d'acconto automatica del 21%, a fine anno dovrai dichiarare manualmente i redditi: nel quadro B se proprietario, nel quadro D se sublocatore. Avere una ricevuta per ogni prenotazione extra piattaforma è utilissimo per tenere traccia degli incassi e fornire documentazione in caso di controlli." },
  { q: "Come funziona la firma elettronica?", a: "Crei il contratto, lo firmi tu come host, poi invii il link all'ospite via WhatsApp o email. L'ospite firma con il dito dal suo smartphone. La firma FES è conforme al regolamento europeo eIDAS e al CAD ed ha piena validità legale." },
  { q: "Come funziona la contabilità?", a: "Selezioni la piattaforma (Airbnb, Booking, diretta), inserisci la cifra lorda, scegli il tipo di commissione e la ritenuta d'acconto. Il sistema calcola automaticamente commissioni, IVA, fee di transazione e tassa di soggiorno. Puoi registrare tutte le spese e visualizzare grafici dettagliati." },
  { q: "In quali lingue posso creare i contratti?", a: "5 lingue: italiano, inglese, francese, spagnolo e tedesco. Ideale per ospiti internazionali su Airbnb, Booking.com e altre piattaforme." },
  { q: "Quanto costa?", a: "7 giorni di prova gratuita. Mensile €6,99/mese, Annuale €49,99/anno (-40%). Contratti illimitati, ricevute illimitate, contabilità completa, firma remota inclusi." },
  { q: "Per chi è pensato?", a: "Host privati di Airbnb e Booking, proprietari di B&B e case vacanze, property manager, gestori di appartamenti turistici e agenzie di affitti brevi in tutta Italia." },
  { q: "Devo registrare il contratto all'Agenzia delle Entrate?", a: "No, i contratti di locazione turistica sotto i 30 giorni non vanno registrati. I redditi vanno dichiarati con cedolare secca al 21% (26% dalla seconda unità)." },
  { q: "La piattaforma è conforme al GDPR?", a: "Sì. I PDF vengono eliminati dopo 30 giorni. I dati FES della firma sono solo nel PDF e non sui server. Privacy by design." },
  { q: "Posso usarla per Airbnb e Booking?", a: "Assolutamente sì. Progettata per host su Airbnb, Booking.com, Vrbo e tutte le OTA. Crei contratto, generi ricevute, calcoli guadagni da un'unica piattaforma." },
  { q: "Come creo una ricevuta?", a: "Dalla dashboard vai in Ricevute. Compila i dati, inserisci importi. Il totale si calcola in automatico. Firma online e scarica il PDF. Se supera €77,47 include spazio per la marca da bollo." },
];

const jsonLd = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "SoftwareApplication",
      "name": "Locazione Turistica",
      "alternateName": ["Locazione-turistica.it", "Vita da Host - Contratti"],
      "url": "https://contratti.vitadahost.it",
      "applicationCategory": "BusinessApplication",
      "applicationSubCategory": "Property Management Software",
      "operatingSystem": "Web, iOS, Android",
      "inLanguage": ["it-IT", "en", "fr", "es", "de"],
      "description": "L'app per tutti gli host italiani: contratti locazione turistica firma FES eIDAS, ricevute con marca da bollo, contabilità OTA-centrica (Airbnb/Booking/Vrbo) con cedolare 21/26%, gestione pulizie con link staff WhatsApp e realtime, QR Code Generator (statici, WiFi, dinamici editabili), tassa di soggiorno automatica con inserimento manuale, archivio scadenze fiscali, calendario eventi 2026/2027 con alert prezzi (419 eventi tra concerti, fiere, sportivi, festival), import iCal Airbnb/Booking automatico. In arrivo: check-in digitale ospiti + invio schedine Polizia + statistica ROSS 1000 ISTAT. Conforme CIN L. 213/2023 e GDPR.",
      "offers": [
        { "@type": "Offer", "name": "Mensile", "price": "6.99", "priceCurrency": "EUR", "priceValidUntil": "2027-12-31", "availability": "https://schema.org/InStock" },
        { "@type": "Offer", "name": "Annuale", "price": "49.99", "priceCurrency": "EUR", "priceValidUntil": "2027-12-31", "availability": "https://schema.org/InStock" },
        { "@type": "Offer", "name": "Plugin QR Code lifetime", "price": "2.99", "priceCurrency": "EUR", "availability": "https://schema.org/InStock" }
      ],
      "featureList": [
        "Contratti locazione turistica 5 lingue (IT, EN, FR, ES, DE) con firma FES eIDAS",
        "Ricevute affitto breve PDF con marca da bollo automatica sopra €77,47",
        "Contabilità OTA-centrica: bonifici Airbnb/Booking/Vrbo + entrate dirette con cedolare 21%/26%",
        "Gestione Pulizie: coordina staff con link tokenizzato WhatsApp, foto sporcizia/danni, realtime SSE",
        "QR Code Generator: statici, WiFi, dinamici editabili (€2.99 una tantum lifetime)",
        "Tassa di Soggiorno: inserimento manuale o import iCal con calcolo automatico per Comune",
        "Calendario Eventi 2026/2027: 419 eventi (266 concerti, 82 fiere AEFI/IEG, 40 sportivi inc. Sei Nazioni Rugby Roma + America's Cup Napoli, 31 festival inc. Sanremo 2027) con alert prezzi 60gg prima",
        "Import iCal Airbnb/Booking/Vrbo automatico ogni 30 min",
        "Calendario scadenze fiscali integrato (cedolare, IMU, TARI)",
        "Gestione proprietà multiple con CIN, dati catastali, capienza, foro competente",
        "Firma salvata proprietario: 1-click su ogni contratto",
        "Override proprietario contratti: gestisci per terzi",
        "Compliant GDPR: auto-rimozione PDF dopo 30 giorni",
        "Conforme CIN L. 213/2023",
        "In arrivo: check-in digitale ospiti + invio schedine Polizia automatico + statistica ROSS 1000 ISTAT"
      ],
      "author": { "@type": "Organization", "name": "Vita da Host", "url": "https://www.vitadahost.it" },
      "aggregateRating": { "@type": "AggregateRating", "ratingValue": "4.9", "ratingCount": "85", "bestRating": "5" },
      "audience": {
        "@type": "Audience",
        "audienceType": "Host affitti brevi, proprietari B&B, gestori case vacanze, affittacamere, property manager, host Airbnb, host Booking.com, host Vrbo, proprietari appartamenti turistici, piccole agenzie immobiliari"
      }
    },
    {
      "@type": "Organization",
      "name": "Vita da Host",
      "legalName": "Vita da Host di Sergio Battelli",
      "url": "https://www.vitadahost.it",
      "logo": "https://contratti.vitadahost.it/icon.png",
      "founder": { "@type": "Person", "name": "Sergio Battelli" },
      "address": { "@type": "PostalAddress", "addressLocality": "Genova", "addressRegion": "Liguria", "addressCountry": "IT" },
      "vatID": "IT02903680995",
      "email": "team@vitadahost.it",
      "sameAs": ["https://www.vitadahost.it"]
    },
    { "@type": "FAQPage", "mainEntity": faqData.map(f => ({ "@type": "Question", "name": f.q, "acceptedAnswer": { "@type": "Answer", "text": f.a } })) },
    { "@type": "WebSite", "name": "Locazione Turistica", "url": "https://contratti.vitadahost.it", "inLanguage": "it-IT", "publisher": { "@type": "Organization", "name": "Vita da Host" } },
    { "@type": "BreadcrumbList", "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://contratti.vitadahost.it/" },
      { "@type": "ListItem", "position": 2, "name": "Registrati", "item": "https://contratti.vitadahost.it/register" },
      { "@type": "ListItem", "position": 3, "name": "Contatti", "item": "https://contratti.vitadahost.it/contact" }
    ]},
    {
      "@type": "HowTo",
      "name": "Come creare un contratto di locazione turistica online",
      "description": "Guida rapida per host: crea, firma e condividi un contratto di affitto breve conforme alla normativa italiana in meno di 5 minuti.",
      "totalTime": "PT5M",
      "tool": [{ "@type": "HowToTool", "name": "Locazione Turistica - Piattaforma SaaS" }],
      "step": [
        { "@type": "HowToStep", "position": 1, "name": "Registrati gratis", "text": "Crea un account su contratti.vitadahost.it e completa il profilo del proprietario con dati anagrafici, codice fiscale e dati della struttura (CIN, catastali).", "url": "https://contratti.vitadahost.it/register" },
        { "@type": "HowToStep", "position": 2, "name": "Crea il contratto", "text": "Seleziona la proprietà, la lingua del contratto (IT/EN/FR/ES/DE), date di check-in e check-out, importo e modalità di pagamento." },
        { "@type": "HowToStep", "position": 3, "name": "Firma il contratto", "text": "Come proprietario firmi dalla dashboard con signature pad touch-friendly. Il link all'ospite si sblocca solo dopo la tua firma." },
        { "@type": "HowToStep", "position": 4, "name": "Condividi con l'ospite", "text": "Invia il link firma via WhatsApp o email. L'ospite compila i suoi dati, carica i documenti e firma dal telefono in meno di 2 minuti." },
        { "@type": "HowToStep", "position": 5, "name": "Scarica il PDF con audit FES", "text": "Ricevi la notifica email di firma completata e scarichi il PDF con audit log (IP, User-Agent, timestamp, hash SHA-256) conforme eIDAS." }
      ]
    },
    {
      "@type": "ItemList",
      "name": "Per chi è Locazione Turistica",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Host Airbnb" },
        { "@type": "ListItem", "position": 2, "name": "Host Booking.com" },
        { "@type": "ListItem", "position": 3, "name": "Host Vrbo e HomeAway" },
        { "@type": "ListItem", "position": 4, "name": "Host B&B.it" },
        { "@type": "ListItem", "position": 5, "name": "Proprietari di B&B (Bed and Breakfast)" },
        { "@type": "ListItem", "position": 6, "name": "Gestori di case vacanze (CAV)" },
        { "@type": "ListItem", "position": 7, "name": "Affittacamere" },
        { "@type": "ListItem", "position": 8, "name": "Gestori di appartamenti turistici" },
        { "@type": "ListItem", "position": 9, "name": "Property manager con più strutture" },
        { "@type": "ListItem", "position": 10, "name": "Host di prenotazioni dirette (non OTA)" }
      ]
    }
  ]
};

function useInView(threshold = 0.15) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVisible(true); }, { threshold });
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, visible];
}

function FadeIn({ children, className = '', delay = 0 }) {
  const [ref, visible] = useInView();
  return (
    <div ref={ref} className={`transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'} ${className}`} style={{ transitionDelay: `${delay}ms` }}>
      {children}
    </div>
  );
}

export default function LandingPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [heroVisible, setHeroVisible] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [menuHinted, setMenuHinted] = useState(() => {
    if (typeof window === 'undefined') return false;
    return localStorage.getItem('lt_menu_hinted') === '1';
  });

  useEffect(() => { if (user?.owner_profile_complete) navigate('/dashboard', { replace: true }); }, [user, navigate]);
  useEffect(() => { setHeroVisible(true); }, []);
  // Persist hint dismissal so the arrow only flashes the first session
  useEffect(() => {
    if (menuHinted) try { localStorage.setItem('lt_menu_hinted', '1'); } catch {}
  }, [menuHinted]);
  // Lock body scroll when drawer is open
  useEffect(() => {
    if (typeof document === 'undefined') return;
    document.body.style.overflow = mobileMenuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileMenuOpen]);

  return (
    <div className="min-h-screen bg-white text-zinc-900 overflow-x-hidden" data-testid="landing-page">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />
      <style dangerouslySetInnerHTML={{ __html: `
        @keyframes float { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-10px)} }
        @keyframes shimmer { 0%{background-position:-200% 0} 100%{background-position:200% 0} }
        @keyframes pulse-glow { 0%,100%{box-shadow:0 0 0 0 rgba(13,148,136,.4)} 50%{box-shadow:0 0 20px 8px rgba(13,148,136,.12)} }
        @keyframes slide-right { from{transform:translateX(-100%);opacity:0} to{transform:translateX(0);opacity:1} }
        @keyframes draw-line { from{width:0} to{width:100%} }
        .float{animation:float 6s ease-in-out infinite}
        .shimmer-text{background:linear-gradient(90deg,#18181b 30%,#0d9488 50%,#18181b 70%);background-size:200% 100%;-webkit-background-clip:text;-webkit-text-fill-color:transparent;animation:shimmer 4s ease-in-out infinite}
        .glow-btn{animation:pulse-glow 3s ease-in-out infinite}
        .hero-gradient{background:linear-gradient(135deg,#f0fdfa 0%,#fff 40%,#fef3c7 100%)}
        .slide-in{animation:slide-right .6s ease-out both}
        .draw-line{animation:draw-line 1.2s ease-out both}
        .flow-step:hover .flow-num{background:#0d9488;color:#fff}
      `}} />

      {/* Header */}
      <header className="fixed top-0 left-0 right-0 bg-white/80 backdrop-blur-md border-b border-zinc-100 z-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-2 sm:py-3 flex items-center justify-between">
          <Logo size="sm" />
          <nav className="hidden md:flex items-center gap-6 text-xs font-bold uppercase tracking-wider text-zinc-500">
            <a href="#contratti" className="hover:text-zinc-900 transition-colors">Contratti</a>
            <a href="#contabilita" className="hover:text-zinc-900 transition-colors">Contabilità</a>
            <a href="#pulizie" className="hover:text-emerald-700 transition-colors">Pulizie</a>
            <a href="#eventi" className="hover:text-zinc-900 transition-colors">Eventi</a>
            <PluginDropdown navigate={navigate} />
            <a href="#prezzi" className="hover:text-zinc-900 transition-colors">Prezzi</a>
            <a href="#faq" className="hover:text-zinc-900 transition-colors">FAQ</a>
          </nav>
          {/* Desktop CTAs */}
          <div className="hidden md:flex items-center gap-2 sm:gap-3">
            <button onClick={() => navigate('/login')} className="px-4 py-2 text-xs sm:text-sm font-bold uppercase tracking-wider text-zinc-700 hover:text-zinc-900 transition-colors" data-testid="landing-login-btn">Accedi</button>
            <button onClick={() => navigate('/register')} className="px-4 sm:px-5 py-2 text-xs sm:text-sm font-bold uppercase tracking-wider bg-zinc-900 text-white hover:bg-zinc-800 transition-colors" data-testid="landing-register-btn">Registrati</button>
          </div>
          {/* Mobile hamburger with attention pointer */}
          <div className="md:hidden flex items-center relative">
            {!menuHinted && !mobileMenuOpen && (
              <div
                className="absolute right-12 top-1/2 -translate-y-1/2 flex items-center gap-1.5 pointer-events-none animate-pulse"
                aria-hidden="true"
                data-testid="menu-hint"
              >
                <span className="bg-zinc-900 text-white text-[9px] font-black uppercase tracking-[0.18em] px-2 py-1 whitespace-nowrap">Apri il menu</span>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" className="text-zinc-900">
                  <path d="M5 12h14M13 5l7 7-7 7" />
                </svg>
              </div>
            )}
            <button
              onClick={() => { setMobileMenuOpen(true); setMenuHinted(true); }}
              aria-label="Apri il menu"
              className="relative w-10 h-10 flex flex-col items-center justify-center gap-1.5 bg-zinc-900 text-white hover:bg-zinc-800 transition-colors"
              data-testid="hamburger-btn"
            >
              <span className="block w-5 h-0.5 bg-white"></span>
              <span className="block w-5 h-0.5 bg-white"></span>
              <span className="block w-5 h-0.5 bg-white"></span>
            </button>
          </div>
        </div>
      </header>

      {/* Mobile menu drawer */}
      {mobileMenuOpen && (
        <div className="md:hidden fixed inset-0 z-[60]" data-testid="mobile-menu">
          <div
            className="absolute inset-0 bg-zinc-900/80 backdrop-blur-sm animate-in fade-in duration-200"
            onClick={() => setMobileMenuOpen(false)}
          />
          <div className="absolute top-0 right-0 bottom-0 w-[85%] max-w-sm bg-white shadow-2xl flex flex-col animate-in slide-in-from-right duration-300">
            <div className="flex items-center justify-between p-4 border-b border-zinc-100">
              <Logo size="sm" />
              <button
                onClick={() => setMobileMenuOpen(false)}
                aria-label="Chiudi il menu"
                className="w-9 h-9 flex items-center justify-center text-zinc-500 hover:text-zinc-900"
                data-testid="mobile-menu-close"
              >
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
              </button>
            </div>
            {/* Highlighted CTA section */}
            <div className="p-5 bg-gradient-to-br from-emerald-50 to-teal-50 border-b border-zinc-100">
              <p className="text-[10px] font-black uppercase tracking-[0.18em] text-emerald-700 mb-3">Inizia subito</p>
              <button
                onClick={() => { setMobileMenuOpen(false); navigate('/register'); }}
                className="w-full bg-zinc-900 hover:bg-zinc-800 text-white text-sm font-black uppercase tracking-wider px-4 py-3.5 mb-2 transition-colors"
                data-testid="mobile-menu-register"
              >
                Registrati gratis
              </button>
              <button
                onClick={() => { setMobileMenuOpen(false); navigate('/login'); }}
                className="w-full bg-white border-2 border-zinc-900 text-zinc-900 hover:bg-zinc-50 text-sm font-black uppercase tracking-wider px-4 py-3 transition-colors"
                data-testid="mobile-menu-login"
              >
                Accedi
              </button>
            </div>
            {/* Section links */}
            <nav className="flex-1 overflow-y-auto p-3">
              <p className="text-[10px] font-black uppercase tracking-[0.18em] text-zinc-400 px-2 mb-2">Esplora</p>
              {[
                { href: '#contratti', label: 'Contratti' },
                { href: '#ricevute', label: 'Ricevute' },
                { href: '#ricevute-imposta-soggiorno', label: 'Imposta Soggiorno' },
                { href: '#contabilita', label: 'Contabilità' },
                { href: '#pulizie', label: 'Pulizie' },
                { href: '#scadenze', label: 'Scadenze' },
                { href: '#eventi', label: 'Eventi' },
                { href: '#prezzi', label: 'Prezzi' },
                { href: '#faq', label: 'FAQ' },
              ].map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={() => setMobileMenuOpen(false)}
                  className="block px-3 py-3 text-base font-bold text-zinc-800 hover:bg-zinc-50 hover:text-zinc-900 transition-colors border-b border-zinc-50"
                  data-testid={`mobile-menu-link-${link.href.replace('#', '')}`}
                >
                  {link.label}
                  <span className="float-right text-zinc-400">›</span>
                </a>
              ))}

              <p className="text-[10px] font-black uppercase tracking-[0.18em] text-violet-700 px-2 mt-4 mb-2">PLUGIN & WebApp</p>
              {[
                { href: '#plugin-qrcode', label: 'QR Code Generator', sub: 'Statici · WiFi · Dinamici', badge: 'NEW' },
                { href: '#pulizie', label: 'Gestione Pulizie', sub: 'Coordina staff realtime', badge: 'NEW' },
                { href: '#pulizie', label: 'WebApp Pulizie Host', sub: 'Spin-off mobile', badge: 'NEW' },
                { href: '#coming-soon', label: 'Self Check-in ospiti', sub: 'OCR + schedine', badge: 'IN ARRIVO' },
              ].map((p) => (
                <button
                  key={p.label}
                  onClick={() => {
                    setMobileMenuOpen(false);
                    if (p.href) {
                      const el = document.querySelector(p.href);
                      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      else window.location.hash = p.href;
                    }
                  }}
                  className="w-full text-left px-3 py-2.5 hover:bg-violet-50 transition-colors border-b border-zinc-50"
                  data-testid={`mobile-plugin-${(p.href || '').replace(/[^a-z0-9]/gi, '-')}-${p.label.replace(/\s+/g, '-')}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-zinc-900">{p.label}</p>
                      <p className="text-[11px] text-zinc-400 mt-0.5">{p.sub}</p>
                    </div>
                    <span className={`text-[8px] font-black uppercase tracking-wider px-1.5 py-0.5 ${p.badge === 'NEW' ? 'bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white' : 'bg-zinc-100 text-zinc-500'}`}>{p.badge}</span>
                  </div>
                </button>
              ))}

              <a
                href="https://calendly.com/vitadahost/ecosistema"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setMobileMenuOpen(false)}
                className="block mt-4 px-3 py-3 text-sm font-bold text-emerald-700 hover:bg-emerald-50"
              >
                Parla con Sergio · 15min <span className="float-right">↗</span>
              </a>
            </nav>
          </div>
        </div>
      )}

      {/* ═══════════════ HERO ═══════════════ */}
      <section className="hero-gradient pt-24 sm:pt-32 pb-16 sm:pb-24 px-4 sm:px-6 relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\'%3E%3Cg fill=\'%23000\' fill-opacity=\'1\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")' }} />
        <div className="max-w-6xl mx-auto relative">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-14 items-center">
            <div className={`transition-all duration-1000 ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              <p className="text-xs text-zinc-400 mb-3">
                Locazione-turistica.it creato e gestito da{' '}
                <a href="https://www.vitadahost.it" target="_blank" rel="noopener noreferrer" className="text-teal-600 font-semibold underline underline-offset-2" data-testid="vitadahost-link">Vitadahost.it</a>
              </p>
              <a
                href="https://calendly.com/vitadahost/ecosistema"
                target="_blank"
                rel="noopener noreferrer"
                className="group mb-5 inline-flex items-center gap-2 text-xs sm:text-[13px] bg-white/60 backdrop-blur-sm border border-emerald-200/70 rounded-full pl-1.5 pr-3.5 py-1.5 text-zinc-700 hover:border-emerald-400 hover:shadow-sm transition-all"
                data-testid="landing-call-cta-top"
              >
                <span className="inline-flex items-center justify-center w-6 h-6 bg-emerald-500 text-white rounded-full flex-shrink-0">
                  <Bell size={11} weight="fill" />
                </span>
                <span>
                  Vuoi una <strong className="font-black text-emerald-700 group-hover:text-emerald-800">call di 15min con Sergio</strong>? Ti spiega tutto lui.
                </span>
                <ArrowRight size={13} weight="bold" className="text-emerald-600 group-hover:translate-x-0.5 transition-transform" />
              </a>
              <div className="inline-flex items-center gap-2 bg-teal-50 text-teal-700 text-xs font-bold uppercase tracking-wider px-3 py-1.5 mb-5 border border-teal-200 rounded-full"><Clock size={13} weight="bold" /> L'app per tutti gli host italiani</div>
              <h1 className="text-2xl sm:text-3xl lg:text-4xl font-black tracking-tight leading-[1.15] mb-4">
                <span className="shimmer-text">Contratti, Ricevute,</span><br />
                <span className="shimmer-text">Contabilità, Pulizie,</span><br />
                <span className="shimmer-text">QR Code & Eventi 2027</span><br />
                <span>tutto in un'unica app italiana</span>
              </h1>
              <p className="text-sm sm:text-base text-zinc-500 max-w-lg mb-2 leading-relaxed">
                <strong>L'app per tutti gli host italiani</strong>. Crea contratti in 5 lingue con firma FES, ricevute con marca da bollo, contabilità OTA-centrica (Airbnb/Booking/Vrbo), <strong>coordina lo staff pulizie in realtime</strong>, genera <strong>QR Code dinamici editabili</strong> e ricevi alert sui <strong>419 eventi 2026/2027</strong> per alzare i prezzi al momento giusto.
              </p>
              <p className="text-xs text-zinc-400 mb-7">Firma FES eIDAS · 5 lingue · GDPR · Cedolare 21%/26% · iCal Airbnb/Booking · America's Cup Napoli 2027 · Sanremo 2027</p>
              <div className="flex flex-col sm:flex-row gap-3">
                <button onClick={() => navigate('/register')} className="glow-btn inline-flex items-center justify-center gap-3 bg-teal-700 text-white font-bold uppercase tracking-wider text-sm px-8 py-4 rounded-full hover:bg-teal-800 transition-all hover:gap-4" data-testid="landing-cta-btn">Inizia gratis <ArrowRight size={20} weight="bold" /></button>
                <a href="#contratti" className="inline-flex items-center justify-center gap-2 border-2 border-zinc-200 text-zinc-700 font-bold uppercase tracking-wider text-sm px-6 py-4 rounded-full hover:border-zinc-400 transition-colors"><ArrowDown size={18} /> Scopri di più</a>
              </div>
            </div>
            <div className={`relative hidden lg:block transition-all duration-1000 delay-300 ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
              <HeroTechVisual />
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════ 5 PILASTRI ═══════════════ */}
      <section className="py-10 sm:py-16 bg-zinc-50 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">
          <FadeIn><p className="text-center text-xs font-bold uppercase tracking-widest text-teal-600 mb-2">Tutto in un'unica piattaforma</p>
          <h2 className="text-center text-lg sm:text-2xl font-black tracking-tight mb-6 sm:mb-8">Sette strumenti essenziali per ogni host</h2></FadeIn>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-3">
            {[
              { icon: FileText, color: 'bg-zinc-900 text-white', title: 'Contratti', desc: 'Locazione turistica in 5 lingue. Firma FES eIDAS.', tag: '#1', tagColor: 'bg-red-50 text-red-600 border-red-200' },
              { icon: Receipt, color: 'bg-teal-700 text-white', title: 'Ricevute', desc: 'PDF in 10 secondi pronto per essere inviato o stampato.', tag: '#2', tagColor: 'bg-amber-50 text-amber-700 border-amber-200' },
              { icon: Calculator, color: 'bg-emerald-600 text-white', title: 'Contabilità', desc: 'Airbnb, Booking, Vrbo, dirette. Report commercialista.', tag: '#3', tagColor: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
              { icon: Sparkle, color: 'bg-gradient-to-br from-emerald-500 to-teal-700 text-white', title: 'Gestione Pulizie', desc: 'Link staff in WhatsApp · realtime · foto sporcizia.', tag: 'ARRIVATO', tagColor: 'bg-emerald-100 text-emerald-700 border-emerald-300' },
              { icon: Calendar, color: 'bg-blue-600 text-white', title: 'Scadenze', desc: 'ROSS1000, estintore, dichiaraz. annuale, rinnovi.', tag: '#5', tagColor: 'bg-blue-50 text-blue-700 border-blue-200' },
              { icon: Buildings, color: 'bg-sky-700 text-white', title: 'Imposta Soggiorno', desc: 'Adulti × notti / bambini × notti al mese da Booking e Airbnb.', tag: 'NEW', tagColor: 'bg-sky-50 text-sky-700 border-sky-300' },
              { icon: CurrencyEur, color: 'bg-gradient-to-br from-rose-500 to-rose-700 text-white', title: 'Pricing Events', desc: 'Alert concerti, fiere, sport e festival nella tua città.', tag: 'NEW', tagColor: 'bg-rose-50 text-rose-700 border-rose-300' },
            ].map((item, i) => (
              <FadeIn key={i} delay={i * 100}>
                <div className="bg-white border border-zinc-200 rounded-2xl p-4 sm:p-6 transition-all duration-300 hover:shadow-lg hover:-translate-y-1 hover:border-zinc-300 group h-full">
                  <div className="flex items-center gap-3 mb-2">
                    <div className={`w-9 h-9 sm:w-10 sm:h-10 ${item.color} flex items-center justify-center group-hover:scale-110 transition-transform`}><item.icon size={18} weight="bold" /></div>
                    <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 border ${item.tagColor}`}>{item.tag}</span>
                  </div>
                  <h3 className="font-black text-zinc-900 mb-1">{item.title}</h3>
                  <p className="text-xs text-zinc-500 leading-relaxed">{item.desc}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════ NOVITÀ 2026 - Pricing Intelligence ═══════════════ */}
      <section className="py-14 sm:py-24 px-4 sm:px-6 bg-gradient-to-br from-rose-500 via-rose-600 to-pink-700 text-white relative overflow-hidden" id="eventi">
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle at 20% 20%, white 1px, transparent 1px), radial-gradient(circle at 80% 80%, white 1px, transparent 1px)', backgroundSize: '40px 40px' }} />
        <div className="max-w-6xl mx-auto relative">
          <FadeIn>
            <div className="inline-flex items-center gap-2 bg-white/15 border border-white/30 backdrop-blur px-3 py-1.5 text-[10px] sm:text-xs font-bold uppercase tracking-[0.2em] mb-4">
              <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" /> Unico nel suo genere · 2027 in aggiornamento
            </div>
            <h2 className="text-2xl sm:text-4xl lg:text-5xl font-black tracking-tight leading-[1.05] mb-4 max-w-3xl">
              Hai sistemato i tuoi prezzi?
            </h2>
            <p className="text-sm sm:text-lg text-white/85 leading-relaxed max-w-2xl mb-8">
              <strong>419 eventi</strong> in Italia tra concerti, fiere, festival e gare sportive del 2026 e 2027.
              Attiva un alert sulla tua città e ti mandiamo un'email <strong>60 giorni prima</strong> di ogni evento
              per rivedere le tariffe al rialzo. <strong>Ora non potrai più dire "l'avessi saputo prima..."</strong>.
            </p>
          </FadeIn>

          <FadeIn>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mb-8">
              <div className="bg-white/10 border border-white/20 p-4">
                <p className="text-3xl sm:text-4xl font-black">419</p>
                <p className="text-[11px] uppercase tracking-wider text-white/70 mt-1">Eventi 2026 · 2027</p>
              </div>
              <div className="bg-white/10 border border-white/20 p-4">
                <p className="text-3xl sm:text-4xl font-black">70+</p>
                <p className="text-[11px] uppercase tracking-wider text-white/70 mt-1">Città italiane</p>
              </div>
              <div className="bg-white/10 border border-white/20 p-4">
                <p className="text-3xl sm:text-4xl font-black">4</p>
                <p className="text-[11px] uppercase tracking-wider text-white/70 mt-1">Categorie</p>
              </div>
              <div className="bg-white/10 border border-white/20 p-4">
                <p className="text-3xl sm:text-4xl font-black">60gg</p>
                <p className="text-[11px] uppercase tracking-wider text-white/70 mt-1">Anticipo email</p>
              </div>
            </div>
          </FadeIn>

          <FadeIn>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-10">
              {[
                { icon: '🎤', label: 'Concerti', count: '266', detail: 'Vasco Rossi, Pinguini Tattici, Olly, Achille Lauro, Elodie, Eros Ramazzotti · 2027 in aggiornamento' },
                { icon: '🏢', label: 'Fiere AEFI · IEG', count: '82', detail: 'Vinitaly, Salone Mobile, Cosmoprof, Pitti Immagine, HOST Milano, SIGEP · 2027 aggiornato' },
                { icon: '🏆', label: 'Sportivi', count: '40', detail: 'Olimpiadi Milano-Cortina 2026, Sei Nazioni Rugby 2027, America\'s Cup Napoli 2027, F1 Monza, MotoGP' },
                { icon: '🎉', label: 'Festival', count: '31', detail: 'Sanremo 2027, Carnevale Venezia, Umbria Jazz, Mostra del Cinema' },
              ].map((cat, i) => (
                <div key={i} className="bg-white/10 border border-white/20 backdrop-blur p-4 sm:p-5 hover:bg-white/20 transition-colors">
                  <div className="flex items-baseline justify-between mb-2">
                    <span className="text-3xl">{cat.icon}</span>
                    <span className="text-2xl font-black">{cat.count}</span>
                  </div>
                  <p className="text-[11px] font-bold uppercase tracking-wider text-white/80 mb-1">{cat.label}</p>
                  <p className="text-[11px] text-white/60 leading-snug">{cat.detail}</p>
                </div>
              ))}
            </div>
          </FadeIn>

          <FadeIn>
            <div className="bg-white text-zinc-900 p-5 sm:p-7 max-w-3xl">
              <p className="text-xs font-bold uppercase tracking-wider text-rose-600 mb-2">Come funziona</p>
              <ol className="space-y-2 text-sm">
                <li className="flex items-start gap-3"><span className="w-6 h-6 bg-rose-500 text-white flex items-center justify-center font-black text-xs flex-shrink-0">1</span><span><strong>Attivi l'alert</strong> sulla città della tua struttura (puoi averne più di una)</span></li>
                <li className="flex items-start gap-3"><span className="w-6 h-6 bg-rose-500 text-white flex items-center justify-center font-black text-xs flex-shrink-0">2</span><span><strong>60 giorni prima</strong> di ogni concerto/fiera/gara/festival ricevi un'email con tipo evento, data, venue</span></li>
                <li className="flex items-start gap-3"><span className="w-6 h-6 bg-rose-500 text-white flex items-center justify-center font-black text-xs flex-shrink-0">3</span><span><strong>Alzi le tariffe</strong> del 30-50% su Airbnb/Booking prima che il mercato si riempia</span></li>
                <li className="flex items-start gap-3"><span className="w-6 h-6 bg-rose-500 text-white flex items-center justify-center font-black text-xs flex-shrink-0">4</span><span><strong>Incassi di più</strong> per la stessa notte che avresti venduto al prezzo normale</span></li>
              </ol>
              <button onClick={() => navigate('/register')} className="mt-5 inline-flex items-center gap-2 bg-zinc-900 text-white font-bold uppercase tracking-wider text-xs px-6 py-3 hover:bg-zinc-800 transition-colors" data-testid="pricing-events-cta">
                Inizia gratis <ArrowRight size={14} weight="bold" />
              </button>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* ═══════════════ BLOG: ULTIME NOTIZIE ═══════════════ */}
      <LandingBlogSection />

      {/* ═══════════════════════════════════════════════════════════════
         PARTE 1: CONTRATTI LOCAZIONE TURISTICA
      ═══════════════════════════════════════════════════════════════ */}
      <section className="py-12 sm:py-24 px-4 sm:px-6" id="contratti">
        <div className="max-w-5xl mx-auto">
          <FadeIn>
            <div className="flex items-center gap-3 mb-2"><div className="w-8 h-8 bg-zinc-900 flex items-center justify-center"><FileText size={16} weight="bold" className="text-white" /></div><p className="text-xs font-bold uppercase tracking-widest text-zinc-400">Funzionalità #1</p></div>
            <h2 className="text-xl sm:text-3xl font-black tracking-tight mb-2">Contratti di locazione turistica</h2>
            <p className="text-sm text-zinc-500 max-w-2xl mb-8 leading-relaxed">Crea contratti professionali in 5 lingue, falli firmare da remoto con validità legale. Zero carta, zero incontri.</p>
          </FadeIn>

          {/* Flow: Come funziona */}
          <FadeIn>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-0 mb-8">
              {[
                { num: '01', icon: FileText, title: 'Crea il contratto', desc: 'Scegli lingua, alloggio, date e importi. Il contratto si genera con i tuoi dati precompilati.', color: 'border-zinc-900' },
                { num: '02', icon: DeviceMobile, title: 'Invia all\'ospite', desc: 'Condividi il link via WhatsApp, email o SMS. L\'ospite compila i dati dal suo smartphone.', color: 'border-teal-600' },
                { num: '03', icon: PencilSimpleLine, title: 'Firma e scarica', desc: 'L\'ospite firma con il dito. Ricevi notifica. Scarica il PDF con audit log FES.', color: 'border-emerald-600' },
              ].map((step, i) => (
                <div key={i} className={`flow-step relative p-4 sm:p-6 border-l-4 sm:border-l-0 sm:border-t-4 ${step.color} bg-white border-r border-b sm:border-x border-zinc-100 hover:shadow-md transition-all`}>
                  <div className="flex items-center gap-3 mb-2">
                    <span className="flow-num w-7 h-7 border-2 border-zinc-300 flex items-center justify-center text-[11px] font-black text-zinc-400 transition-all">{step.num}</span>
                    <step.icon size={18} weight="bold" className="text-zinc-400" />
                  </div>
                  <h4 className="font-bold text-sm mb-1">{step.title}</h4>
                  <p className="text-xs text-zinc-500 leading-relaxed">{step.desc}</p>
                  {i < 2 && <ArrowRight size={18} weight="bold" className="hidden sm:block absolute -right-[11px] top-1/2 -translate-y-1/2 z-10 text-zinc-300 bg-white" />}
                </div>
              ))}
            </div>
          </FadeIn>

          {/* Tabella: Cosa include il contratto */}
          <FadeIn>
            <div className="bg-zinc-50 border border-zinc-200 p-4 sm:p-8 mb-6">
              <h3 className="font-bold text-xs uppercase tracking-wider text-zinc-500 mb-3">Contenuto del contratto generato</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-1.5">
                {[
                  { section: 'Dati Locatore', items: 'Nome, C.F., residenza, data nascita' },
                  { section: 'Dati Conduttore', items: 'Compilati dall\'ospite via link remoto' },
                  { section: 'Immobile', items: 'Indirizzo, catasto, foglio, particella, CIN' },
                  { section: 'Soggiorno', items: 'Date, notti, check-in/out, max occupanti' },
                  { section: 'Corrispettivo', items: 'Canone, caparra, saldo, tassa soggiorno' },
                  { section: 'Firme digitali', items: 'FES con audit log (IP, timestamp, SHA-256)' },
                ].map((row, i) => (
                  <div key={i} className="flex items-start gap-3 py-2 border-b border-zinc-100 last:border-0">
                    <CaretRight size={14} weight="bold" className="text-teal-600 mt-0.5 flex-shrink-0" />
                    <div><span className="text-xs font-bold text-zinc-900">{row.section}:</span> <span className="text-xs text-zinc-500">{row.items}</span></div>
                  </div>
                ))}
              </div>
            </div>
          </FadeIn>

          {/* Tabella normativa */}
          <FadeIn>
            <div className="bg-white border border-zinc-200 mb-6">
              <div className="bg-zinc-900 text-white px-4 py-2.5 flex items-center gap-2"><Scales size={16} weight="bold" /> <span className="text-[11px] font-bold uppercase tracking-wider">Quadro normativo</span></div>
              <div className="divide-y divide-zinc-100">
                {[
                  { law: 'Legge 431/1998', desc: 'Obbligo forma scritta per tutti i contratti di locazione, incluse le locazioni turistiche.' },
                  { law: 'D.L. 50/2017 (art. 4)', desc: 'Disciplina degli affitti brevi: durata <30 giorni, cedolare secca, obblighi intermediari.' },
                  { law: 'Reg. eIDAS 910/2014', desc: 'Firma elettronica semplice (FES) ha piena validità legale nell\'Unione Europea.' },
                  { law: 'D.Lgs. 82/2005 (CAD)', desc: 'Art. 20: documenti firmati elettronicamente sono validi e rilevanti a tutti gli effetti di legge.' },
                  { law: 'Cass. n. 18214/2015', desc: 'Senza contratto scritto, l\'accordo di locazione turistica è nullo.' },
                  { law: 'GDPR (Reg. 2016/679)', desc: 'Art. 25: Privacy by design. Dati FES nel PDF, non conservati sui server.' },
                ].map((row, i) => (
                  <div key={i} className="flex flex-col sm:flex-row sm:items-start gap-1 sm:gap-4 px-4 py-2.5">
                    <span className="text-[11px] font-bold text-teal-700 whitespace-nowrap sm:min-w-[140px]">{row.law}</span>
                    <span className="text-[11px] text-zinc-600 leading-relaxed">{row.desc}</span>
                  </div>
                ))}
              </div>
            </div>
          </FadeIn>

          {/* Tabella lingue */}
          <FadeIn>
            <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 mb-6">
              {[
                { flag: '🇮🇹', lang: 'Italiano', code: 'ITA' },
                { flag: '🇬🇧', lang: 'English', code: 'ENG' },
                { flag: '🇫🇷', lang: 'Français', code: 'FRA' },
                { flag: '🇪🇸', lang: 'Español', code: 'SPA' },
                { flag: '🇩🇪', lang: 'Deutsch', code: 'TED' },
              ].map((l, i) => (
                <div key={i} className="bg-zinc-50 border border-zinc-200 p-2.5 sm:p-3 text-center hover:border-teal-300 hover:bg-teal-50 transition-all cursor-default">
                  <span className="text-xl sm:text-2xl block mb-0.5">{l.flag}</span>
                  <p className="text-[11px] font-bold text-zinc-900">{l.lang}</p>
                  <p className="text-[10px] text-zinc-400">{l.code}</p>
                </div>
              ))}
            </div>
          </FadeIn>

          {/* Firma FES showcase */}
          <FadeIn>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
              <div className="bg-zinc-900 text-white p-4 sm:p-6">
                <h4 className="text-[11px] font-bold uppercase tracking-wider text-zinc-400 mb-3">Audit log integrato nel PDF</h4>
                <div className="space-y-1.5 font-mono text-[10px] sm:text-[11px] text-zinc-400">
                  <div className="flex gap-2"><span className="text-teal-400">IP:</span> <span>93.47.xxx.xxx</span></div>
                  <div className="flex gap-2"><span className="text-teal-400">Timestamp:</span> <span>2026-04-18T14:32:07Z</span></div>
                  <div className="flex gap-2"><span className="text-teal-400">User-Agent:</span> <span>iPhone/Safari 19.2</span></div>
                  <div className="flex gap-2"><span className="text-teal-400">Session:</span> <span>a7f2c4e1-9b3d-...</span></div>
                  <div className="border-t border-zinc-700 pt-2 mt-2"><span className="text-amber-400">SHA-256:</span> <span className="break-all">e3b0c44298fc1c14...</span></div>
                </div>
              </div>
              <div className="bg-teal-50 border border-teal-200 p-5 sm:p-6 flex flex-col justify-center">
                <ShieldCheck size={32} weight="bold" className="text-teal-700 mb-3" />
                <h4 className="font-bold text-sm text-zinc-900 mb-2">Firma FES conforme eIDAS</h4>
                <p className="text-xs text-zinc-600 leading-relaxed">Ogni firma cattura IP, timestamp, device e genera un hash SHA-256 per garantire l'integrità del documento. Tutto incorporato nel PDF.</p>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════
         PARTE 2: RICEVUTE AFFITTO BREVE
      ═══════════════════════════════════════════════════════════════ */}
      <section className="py-12 sm:py-24 bg-gradient-to-b from-teal-50 to-white px-4 sm:px-6" id="ricevute">
        <div className="max-w-5xl mx-auto">
          <FadeIn>
            <div className="flex items-center gap-3 mb-2"><div className="w-8 h-8 bg-teal-700 flex items-center justify-center"><Receipt size={16} weight="bold" className="text-white" /></div><p className="text-xs font-bold uppercase tracking-widest text-zinc-400">Funzionalità #2</p></div>
            <h2 className="text-xl sm:text-3xl font-black tracking-tight mb-2">Ricevute di affitto breve</h2>
            <p className="text-sm text-zinc-500 max-w-2xl mb-8 leading-relaxed">Genera ricevute professionali in 20 secondi. Firma, scarica il PDF, stampa o invia.</p>
          </FadeIn>

          {/* Ricevuta mockup */}
          <FadeIn>
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 sm:gap-6 mb-8">
              <div className="lg:col-span-3 bg-white border-2 border-zinc-200 shadow-lg p-4 sm:p-6">
                <div className="border-b-2 border-zinc-900 pb-2 mb-3 flex items-center justify-between">
                  <div><p className="text-xs sm:text-sm font-black uppercase tracking-wider">Ricevuta di pagamento</p><p className="text-[10px] text-zinc-400">Locazione turistica</p></div>
                  <div className="text-right"><p className="text-[11px] font-bold">N° 001/2026</p><p className="text-[10px] text-zinc-400">14/04/2026</p></div>
                </div>
                <div className="grid grid-cols-2 gap-4 text-xs mb-4">
                  <div><p className="font-bold text-zinc-400 uppercase text-[10px] mb-1">Locatore</p><p className="font-bold">Mario Rossi</p><p className="text-zinc-500">C.F. RSSMRA80A01D969Z</p></div>
                  <div><p className="font-bold text-zinc-400 uppercase text-[10px] mb-1">Ospite</p><p className="font-bold">John Smith</p><p className="text-zinc-500">Passport AB1234567</p></div>
                </div>
                <div className="bg-zinc-50 p-3 mb-4 text-xs"><p className="text-zinc-400 text-[10px] font-bold uppercase mb-1">Immobile</p><p>Via Roma 10, Genova (GE) · CIN-001</p><p className="text-zinc-400">Dal 15/04/2026 al 18/04/2026 (3 notti)</p></div>
                <div className="space-y-1 text-xs border-b border-zinc-200 pb-3 mb-3">
                  <div className="flex justify-between"><span className="text-zinc-500">Canone di locazione</span><span>€ 450,00</span></div>
                  <div className="flex justify-between"><span className="text-zinc-500">Pulizie finali</span><span>€ 80,00</span></div>
                  <div className="flex justify-between"><span className="text-zinc-500">Tassa di soggiorno</span><span>€ 12,00</span></div>
                </div>
                <div className="flex justify-between items-center"><span className="text-sm font-black uppercase">Totale</span><span className="text-lg font-black">€ 542,00</span></div>
                <div className="mt-3 bg-amber-50 border border-amber-200 p-2 text-[10px] text-amber-700 flex items-center gap-1"><Warning size={12} weight="fill" /> Importo superiore a €77,47: marca da bollo da €2,00 obbligatoria</div>
              </div>
              <div className="lg:col-span-2 grid grid-cols-3 lg:grid-cols-1 gap-2 sm:gap-4">
                {[
                  { icon: PencilSimpleLine, title: 'Compila in 20 secondi', desc: 'Dati precompilati dal tuo profilo. Solo importi e date da inserire.' },
                  { icon: PencilSimpleLine, title: 'Firma online', desc: 'Firma la ricevuta direttamente dal tuo smartphone o PC.' },
                  { icon: Printer, title: 'PDF pronto', desc: 'Scarica e stampa. Spazio per marca da bollo se > €77,47.' },
                ].map((f, i) => (
                  <div key={i} className="bg-white border border-zinc-200 p-3 sm:p-4 flex flex-col sm:flex-row items-start gap-2 sm:gap-3 hover:border-teal-300 transition-colors">
                    <f.icon size={18} weight="bold" className="text-teal-600 flex-shrink-0" />
                    <div><h4 className="font-bold text-[11px] text-zinc-900">{f.title}</h4><p className="text-[10px] text-zinc-500 mt-0.5 hidden sm:block">{f.desc}</p></div>
                  </div>
                ))}
              </div>
            </div>
          </FadeIn>

          {/* Tabella: Regole marca da bollo */}
          <FadeIn>
            <div className="bg-white border border-zinc-200 overflow-hidden mb-6 rounded-2xl">
              <div className="bg-amber-50 border-b border-amber-200 px-4 py-2.5"><span className="text-[11px] font-bold uppercase tracking-wider text-amber-800">Marca da bollo — DPR 642/1972</span></div>
              <div className="grid grid-cols-3 divide-x divide-zinc-100">
                <div className="p-3 sm:p-5 text-center"><p className="text-lg sm:text-2xl font-black text-emerald-600 mb-0.5">€0–77</p><p className="text-[10px] sm:text-xs text-zinc-500">Nessuna marca</p></div>
                <div className="p-3 sm:p-5 text-center bg-amber-50"><p className="text-lg sm:text-2xl font-black text-amber-700 mb-0.5">€77,47+</p><p className="text-[10px] sm:text-xs text-zinc-700 font-bold">€2,00</p></div>
                <div className="p-3 sm:p-5 text-center"><div className="w-6 h-6 sm:w-8 sm:h-8 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-1"><Warning size={14} weight="fill" className="text-red-500" /></div><p className="text-[10px] sm:text-xs text-zinc-700 font-bold">MAI postdatata</p></div>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════
         PARTE 2b: RICEVUTA IMPOSTA DI SOGGIORNO
      ═══════════════════════════════════════════════════════════════ */}
      <section className="py-12 sm:py-24 bg-gradient-to-b from-white via-amber-50/40 to-white px-4 sm:px-6 relative overflow-hidden" id="ricevute-imposta-soggiorno">
        {/* Decorative background */}
        <div className="absolute -top-24 -right-24 w-96 h-96 bg-gradient-to-br from-amber-200/50 to-transparent rounded-full blur-3xl pointer-events-none" />
        <div className="absolute -bottom-32 -left-32 w-96 h-96 bg-gradient-to-tr from-teal-100/40 to-transparent rounded-full blur-3xl pointer-events-none" />

        <div className="max-w-5xl mx-auto relative">
          <FadeIn>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-8 h-8 bg-amber-500 rounded-lg flex items-center justify-center shadow-md">
                <CurrencyEur size={16} weight="bold" className="text-white" />
              </div>
              <p className="text-xs font-bold uppercase tracking-widest text-zinc-400">Funzionalità #2b · Novità 2026</p>
              <span className="inline-flex items-center px-2 py-0.5 text-[9px] font-black uppercase tracking-wider bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-full">Nuovo</span>
            </div>
            <h2 className="text-xl sm:text-3xl font-black tracking-tight mb-2 font-geist">
              Ricevute <span className="text-amber-600">Imposta di Soggiorno</span>
            </h2>
            <p className="text-sm text-zinc-500 max-w-2xl mb-8 leading-relaxed">
              Ogni Comune ha la sua tariffa. Genera una ricevuta ufficiale per ciascun ospite con il calcolo automatico
              <strong className="text-zinc-900"> (ospiti paganti × notti × tariffa)</strong>, gestione dei minori esenti,
              e PDF pronto da stampare o inviare via WhatsApp al check-in.
            </p>
          </FadeIn>

          {/* Ricevuta mockup + features */}
          <FadeIn>
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 sm:gap-6 mb-8">
              {/* Mockup ricevuta imposta */}
              <div className="lg:col-span-3 bg-white border border-zinc-200 shadow-xl p-4 sm:p-6 rounded-2xl relative overflow-hidden group hover:shadow-2xl transition-shadow duration-500">
                <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-amber-400 via-orange-500 to-amber-600" />

                <div className="border-b border-zinc-200 pb-3 mb-4 flex items-start justify-between">
                  <div>
                    <p className="text-xs sm:text-sm font-black uppercase tracking-wider text-zinc-900">Ricevuta imposta di soggiorno</p>
                    <p className="text-[10px] text-zinc-400">Tributo comunale · Art. 4 D.Lgs. 23/2011</p>
                  </div>
                  <div className="text-right">
                    <p className="text-[11px] font-bold">N° IS-2026-042</p>
                    <p className="text-[10px] text-zinc-400">14/04/2026</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 text-xs mb-4">
                  <div>
                    <p className="font-bold text-zinc-400 uppercase text-[10px] mb-1">Locatore</p>
                    <p className="font-bold">Mario Rossi</p>
                    <p className="text-zinc-500">C.F. RSSMRA80A01D969Z</p>
                  </div>
                  <div>
                    <p className="font-bold text-zinc-400 uppercase text-[10px] mb-1">Ospite</p>
                    <p className="font-bold">John Smith</p>
                    <p className="text-zinc-500">Passport AB1234567</p>
                  </div>
                </div>

                <div className="bg-zinc-50 rounded-xl p-3 mb-4 text-xs">
                  <p className="text-zinc-400 text-[10px] font-bold uppercase mb-1">Immobile · Comune di Genova</p>
                  <p>Via Roma 10, Genova (GE)</p>
                  <p className="text-zinc-400">Dal 15/04/2026 al 18/04/2026</p>
                </div>

                {/* Calcolo animato */}
                <div className="space-y-1.5 text-xs border-y border-zinc-200 py-3 mb-3">
                  <div className="flex justify-between items-center">
                    <span className="text-zinc-500 flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" /> Totale ospiti</span>
                    <span className="font-bold">4</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-zinc-500 pl-3.5">di cui esenti (minori)</span>
                    <span className="text-zinc-500">1</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-zinc-900 pl-3.5 font-semibold">Ospiti paganti</span>
                    <span className="font-black text-amber-700">3</span>
                  </div>
                  <div className="flex justify-between items-center pt-1">
                    <span className="text-zinc-500 flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" style={{ animationDelay: '400ms' }} /> Notti</span>
                    <span className="font-bold">3</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-zinc-500 flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" style={{ animationDelay: '800ms' }} /> Tariffa Comune / notte</span>
                    <span className="font-bold">€ 3,00</span>
                  </div>
                  <div className="flex justify-between items-center pt-2 mt-1 border-t border-dashed border-zinc-200">
                    <span className="text-[10px] text-zinc-400 italic">3 paganti × 3 notti × € 3,00</span>
                  </div>
                </div>

                <div className="flex justify-between items-center bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl px-3 py-2.5">
                  <span className="text-sm font-black uppercase text-zinc-900">Totale riscosso</span>
                  <span className="text-lg sm:text-xl font-black text-amber-700 tabular-nums">€ 27,00</span>
                </div>

                <div className="mt-3 bg-teal-50 border border-teal-200 rounded-lg p-2 text-[10px] text-teal-800 flex items-start gap-1.5">
                  <Check size={12} weight="bold" className="text-teal-600 flex-shrink-0 mt-0.5" />
                  <span><strong>Non soggetto a IVA</strong> — natura di tributo locale. Nessun impatto sul reddito imponibile del locatore.</span>
                </div>
              </div>

              {/* Feature list */}
              <div className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-1 gap-2 sm:gap-4">
                {[
                  { icon: Calculator, title: 'Calcolo automatico', desc: 'Ospiti × notti × tariffa comunale, con esenzione minori.', color: 'from-amber-500 to-orange-500' },
                  { icon: Printer, title: 'PDF ufficiale', desc: 'Layout conforme al regolamento comunale. Firma + timbro.', color: 'from-teal-500 to-teal-700' },
                  { icon: WhatsappLogo, title: 'Invia al check-in', desc: 'Un tocco → PDF via WhatsApp all\'ospite. Zero email.', color: 'from-emerald-500 to-teal-600' },
                ].map((f, i) => (
                  <div key={i} className="bg-white border border-zinc-200 rounded-2xl p-3 sm:p-4 flex flex-col sm:flex-row lg:flex-col items-start gap-3 hover:border-amber-300 hover:shadow-md hover:-translate-y-1 transition-all duration-300 group">
                    <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${f.color} text-white flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform`}>
                      <f.icon size={18} weight="bold" />
                    </div>
                    <div>
                      <h4 className="font-bold text-[12px] text-zinc-900">{f.title}</h4>
                      <p className="text-[10px] text-zinc-500 mt-0.5 leading-relaxed">{f.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </FadeIn>

          {/* Info banner tariffe per comuni */}
          <FadeIn>
            <div className="bg-white border border-zinc-200 rounded-2xl overflow-hidden shadow-sm">
              <div className="bg-gradient-to-r from-amber-100 to-orange-100 border-b border-amber-200 px-4 py-2.5">
                <span className="text-[11px] font-bold uppercase tracking-wider text-amber-900">Tariffe esempio (2026) — verifica sempre il regolamento del tuo Comune</span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 divide-x divide-y sm:divide-y-0 divide-zinc-100">
                {[
                  { city: 'Roma', rate: '€ 4,00 – 10,00' },
                  { city: 'Milano', rate: '€ 3,00 – 5,00' },
                  { city: 'Firenze', rate: '€ 3,00 – 8,00' },
                  { city: 'Venezia', rate: '€ 1,00 – 5,00' },
                ].map((c, i) => (
                  <div key={i} className="p-3 sm:p-4 text-center hover:bg-amber-50 transition-colors">
                    <p className="text-xs font-black text-zinc-900 uppercase tracking-wider">{c.city}</p>
                    <p className="text-[10px] sm:text-xs text-amber-700 font-bold mt-1 tabular-nums">{c.rate}</p>
                    <p className="text-[9px] text-zinc-400">per ospite/notte</p>
                  </div>
                ))}
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════
         PARTE 3: CONTABILITÀ
      ═══════════════════════════════════════════════════════════════ */}
      <section className="py-12 sm:py-24 bg-zinc-900 text-white px-4 sm:px-6 relative overflow-hidden" id="contabilita">
        <div className="absolute inset-0 opacity-10"><video autoPlay muted loop playsInline className="w-full h-full object-cover"><source src={VIDEO_BG} type="video/mp4" /></video></div>
        <div className="max-w-5xl mx-auto relative z-10">
          <FadeIn>
            <div className="flex items-center gap-3 mb-2"><div className="w-8 h-8 bg-emerald-500 flex items-center justify-center"><Calculator size={16} weight="bold" className="text-white" /></div><span className="text-xs font-bold uppercase tracking-widest text-emerald-400">Funzionalità #3 · Semplificata</span></div>
            <h2 className="text-2xl sm:text-4xl font-black tracking-tight mb-3 leading-tight max-w-3xl">
              Abbiamo semplificato tutto.<br />
              <span className="text-emerald-400">Tutto sott'occhio in 3 numeri.</span>
            </h2>
            <p className="text-sm sm:text-base text-zinc-400 max-w-2xl mb-10 leading-relaxed">
              Basta calcoli a manina, basta fogli Excel. <strong className="text-white">Inserisci gli importi versati dalle OTA (Airbnb, Booking, Vrbo) e le tue entrate dirette.</strong> Il resto lo facciamo noi: cedolare 21% / 26%, spese deducibili, profitto netto. Tieni <strong className="text-white">sempre tutto sotto controllo</strong>.
            </p>
          </FadeIn>

          {/* 3 KPI Cards: stesso layout della dashboard */}
          <FadeIn>
            <div className="grid grid-cols-3 gap-3 sm:gap-4 mb-8">
              <div className="bg-white/5 backdrop-blur border border-white/10 p-4 sm:p-6 relative overflow-hidden group hover:bg-white/10 transition-all">
                <div className="absolute top-0 left-0 right-0 h-0.5 bg-emerald-500" />
                <div className="flex items-center gap-1.5 mb-2 sm:mb-3">
                  <div className="w-6 h-6 sm:w-7 sm:h-7 bg-emerald-500 flex items-center justify-center"><TrendUp size={12} weight="bold" className="text-white" /></div>
                  <p className="text-[9px] sm:text-[10px] font-black uppercase tracking-[0.18em] text-emerald-300 truncate">Incassi 2027</p>
                </div>
                <p className="text-2xl sm:text-4xl font-black text-emerald-400 tracking-tight tabular-nums leading-none">€&nbsp;48.320</p>
                <p className="text-[9px] sm:text-[10px] text-zinc-500 mt-2 leading-tight">Bonifici OTA + dirette</p>
              </div>
              <div className="bg-white/5 backdrop-blur border border-white/10 p-4 sm:p-6 relative overflow-hidden group hover:bg-white/10 transition-all">
                <div className="absolute top-0 left-0 right-0 h-0.5 bg-rose-500" />
                <div className="flex items-center gap-1.5 mb-2 sm:mb-3">
                  <div className="w-6 h-6 sm:w-7 sm:h-7 bg-rose-500 flex items-center justify-center"><Receipt size={12} weight="bold" className="text-white" /></div>
                  <p className="text-[9px] sm:text-[10px] font-black uppercase tracking-[0.18em] text-rose-300 truncate">Spese 2027</p>
                </div>
                <p className="text-2xl sm:text-4xl font-black text-rose-400 tracking-tight tabular-nums leading-none">€&nbsp;12.110</p>
                <p className="text-[9px] sm:text-[10px] text-zinc-500 mt-2 leading-tight">IMU, TARI, pulizie, bollette</p>
              </div>
              <div className="bg-white/5 backdrop-blur border border-white/10 p-4 sm:p-6 relative overflow-hidden group hover:bg-white/10 transition-all">
                <div className="absolute top-0 left-0 right-0 h-0.5 bg-white" />
                <div className="flex items-center gap-1.5 mb-2 sm:mb-3">
                  <div className="w-6 h-6 sm:w-7 sm:h-7 bg-white flex items-center justify-center"><CurrencyEur size={12} weight="bold" className="text-zinc-900" /></div>
                  <p className="text-[9px] sm:text-[10px] font-black uppercase tracking-[0.18em] text-white truncate">Profitto 2027</p>
                </div>
                <p className="text-2xl sm:text-4xl font-black text-white tracking-tight tabular-nums leading-none">€&nbsp;36.210</p>
                <p className="text-[9px] sm:text-[10px] text-zinc-500 mt-2 leading-tight">Incassi − Spese</p>
              </div>
            </div>
          </FadeIn>

          {/* Grafico mensile a barre — Entrate vs Spese */}
          <FadeIn>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 sm:gap-4 mb-6">
              <div className="lg:col-span-2 bg-white/5 backdrop-blur border border-white/10 p-5 sm:p-7">
                <div className="flex items-center justify-between mb-5 flex-wrap gap-2">
                  <p className="text-[11px] font-black uppercase tracking-wider text-emerald-300">Andamento mensile · 2027</p>
                  <div className="flex items-center gap-3 text-[10px] text-zinc-400">
                    <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 bg-emerald-500"></span>Entrate</span>
                    <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 bg-rose-500"></span>Spese</span>
                  </div>
                </div>
                {/* Chart */}
                <div className="relative h-44 sm:h-56">
                  <svg viewBox="0 0 600 220" className="w-full h-full" preserveAspectRatio="none">
                    {/* horizontal grid lines */}
                    {[0, 1, 2, 3, 4].map(i => (
                      <line key={i} x1="0" y1={i * 50} x2="600" y2={i * 50} stroke="rgba(255,255,255,0.05)" strokeWidth="1" />
                    ))}
                    {[
                      { m: 'Gen', e: 3.8, s: 1.1 },
                      { m: 'Feb', e: 4.2, s: 0.9 },
                      { m: 'Mar', e: 3.5, s: 1.0 },
                      { m: 'Apr', e: 4.6, s: 1.2 },
                      { m: 'Mag', e: 5.1, s: 1.3 },
                      { m: 'Giu', e: 6.4, s: 1.1 },
                      { m: 'Lug', e: 7.2, s: 0.8 },
                      { m: 'Ago', e: 8.1, s: 1.4 },
                      { m: 'Set', e: 6.8, s: 1.0 },
                      { m: 'Ott', e: 4.9, s: 0.9 },
                      { m: 'Nov', e: 3.6, s: 1.3 },
                      { m: 'Dic', e: 4.1, s: 1.1 },
                    ].map((d, i) => {
                      const x = 16 + i * 49;
                      const eH = d.e * 22;
                      const sH = d.s * 22;
                      return (
                        <g key={i}>
                          <rect x={x} y={200 - eH} width="18" height={eH} fill="#10b981" rx="1">
                            <animate attributeName="height" from="0" to={eH} dur="0.8s" fill="freeze" begin={`${i * 0.05}s`} />
                            <animate attributeName="y" from="200" to={200 - eH} dur="0.8s" fill="freeze" begin={`${i * 0.05}s`} />
                          </rect>
                          <rect x={x + 20} y={200 - sH} width="9" height={sH} fill="#f43f5e" opacity="0.85" rx="1">
                            <animate attributeName="height" from="0" to={sH} dur="0.8s" fill="freeze" begin={`${i * 0.05 + 0.1}s`} />
                            <animate attributeName="y" from="200" to={200 - sH} dur="0.8s" fill="freeze" begin={`${i * 0.05 + 0.1}s`} />
                          </rect>
                          <text x={x + 13} y="215" fill="rgba(255,255,255,0.4)" fontSize="10" textAnchor="middle" fontWeight="700" letterSpacing="0.5">{d.m}</text>
                        </g>
                      );
                    })}
                  </svg>
                </div>
                <p className="text-[10px] text-zinc-500 mt-3 leading-relaxed">Picco estivo a luglio e agosto · Spese costanti tutto l'anno · Tasso conversione 75%</p>
              </div>

              {/* Donut chart: mix bonifici per piattaforma */}
              <div className="bg-white/5 backdrop-blur border border-white/10 p-5 sm:p-7">
                <p className="text-[11px] font-black uppercase tracking-wider text-emerald-300 mb-4">Mix bonifici</p>
                <div className="relative flex items-center justify-center mb-4">
                  <svg viewBox="0 0 120 120" className="w-36 h-36">
                    {/* Donut: 45% Airbnb, 30% Booking, 20% Diretta, 5% Vrbo */}
                    {(() => {
                      const segments = [
                        { color: '#FF5A5F', pct: 45, label: 'Airbnb' },
                        { color: '#003580', pct: 30, label: 'Booking' },
                        { color: '#10b981', pct: 20, label: 'Diretta' },
                        { color: '#6366f1', pct: 5, label: 'Vrbo' },
                      ];
                      const r = 50;
                      const c = 2 * Math.PI * r;
                      let offset = 0;
                      return segments.map((s, i) => {
                        const len = (s.pct / 100) * c;
                        const el = (
                          <circle
                            key={i}
                            cx="60" cy="60" r={r}
                            fill="transparent"
                            stroke={s.color}
                            strokeWidth="16"
                            strokeDasharray={`${len} ${c - len}`}
                            strokeDashoffset={-offset}
                            transform="rotate(-90 60 60)"
                            style={{ transition: 'stroke-dasharray 1s ease-out' }}
                          />
                        );
                        offset += len;
                        return el;
                      });
                    })()}
                    <text x="60" y="58" textAnchor="middle" fill="#fff" fontSize="13" fontWeight="900">€48k</text>
                    <text x="60" y="73" textAnchor="middle" fill="rgba(255,255,255,0.5)" fontSize="8" fontWeight="700" letterSpacing="1">TOTALE</text>
                  </svg>
                </div>
                <div className="space-y-1.5">
                  {[
                    { color: '#FF5A5F', label: 'Airbnb', pct: '45%' },
                    { color: '#003580', label: 'Booking', pct: '30%' },
                    { color: '#10b981', label: 'Diretta', pct: '20%' },
                    { color: '#6366f1', label: 'Vrbo', pct: '5%' },
                  ].map((s, i) => (
                    <div key={i} className="flex items-center justify-between text-[10px]">
                      <span className="inline-flex items-center gap-1.5"><span className="w-2 h-2" style={{ backgroundColor: s.color }}></span><span className="text-zinc-400">{s.label}</span></span>
                      <span className="font-black tabular-nums">{s.pct}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </FadeIn>

          {/* Come funziona — 3 step semplici */}
          <FadeIn>
            <div className="bg-white/5 backdrop-blur border border-white/10 p-5 sm:p-7 mb-8">
              <p className="text-[11px] font-black uppercase tracking-wider text-emerald-300 mb-5">In 3 mosse, niente di più</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
                {[
                  { num: '1', icon: TrendUp, color: 'bg-emerald-500', title: 'Inserisci i bonifici OTA', desc: 'Per ogni accredito Airbnb, Booking o Vrbo digiti l\'importo netto ricevuto. Le commissioni sono già scontate alla fonte.' },
                  { num: '2', icon: CurrencyEur, color: 'bg-emerald-600', title: 'Aggiungi le entrate dirette', desc: 'Per le tue prenotazioni dirette inserisci il lordo: calcoliamo cedolare 21% o 26% in automatico (prima o seconda casa).' },
                  { num: '3', icon: ChartBar, color: 'bg-emerald-700', title: 'Vedi il profitto in tempo reale', desc: 'Grafici mensili, mix piattaforma, profitto YTD. Export Excel con foglio Bonifici, Spese e Riepilogo per il commercialista.' },
                ].map((s, i) => (
                  <div key={i} className="flex flex-col">
                    <div className="flex items-center gap-2.5 mb-2">
                      <div className={`w-7 h-7 ${s.color} flex items-center justify-center text-white text-[11px] font-black tabular-nums`}>{s.num}</div>
                      <s.icon size={16} weight="bold" className="text-emerald-400" />
                    </div>
                    <p className="text-sm font-black mb-1 leading-tight">{s.title}</p>
                    <p className="text-[11px] text-zinc-400 leading-relaxed">{s.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </FadeIn>

          {/* Feature list aggiornata */}
          <FadeIn>
            <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3 mb-8">
              {[
                { icon: ChartBar, text: 'Grafici mensili entrate, uscite e profitto' },
                { icon: CurrencyEur, text: 'Cedolare 21% (prima casa) o 26% automatica' },
                { icon: House, text: '13 categorie spese: IMU, TARI, pulizie, bollette' },
                { icon: FileText, text: 'Export Excel: Bonifici · Spese · Riepilogo · CSV' },
              ].map((item, i) => (
                <div key={i} className="bg-white/5 border border-white/10 p-3 sm:p-4 flex items-start gap-2 sm:gap-3 hover:bg-white/10 transition-colors">
                  <item.icon size={16} weight="bold" className="text-emerald-400 flex-shrink-0 mt-0.5" />
                  <p className="text-[11px] sm:text-xs text-zinc-300 leading-relaxed">{item.text}</p>
                </div>
              ))}
            </div>
          </FadeIn>

          <FadeIn>
            <div className="text-center">
              <button onClick={() => navigate('/register')} className="inline-flex items-center gap-3 bg-emerald-500 text-white font-bold uppercase tracking-wider text-sm px-10 py-4 hover:bg-emerald-600 transition-all hover:gap-4" data-testid="contabilita-cta-btn">Prova la contabilità gratis <ArrowRight size={20} weight="bold" /></button>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* ═══════════════ GESTIONE PULIZIE · DISPONIBILE ORA ═══════════════ */}
      <section className="py-12 sm:py-24 px-4 sm:px-6 bg-gradient-to-br from-emerald-50 via-white to-teal-50" id="pulizie">
        <div className="max-w-5xl mx-auto">
          <FadeIn>
            <div className="text-center mb-10">
              <div className="inline-flex items-center gap-2 bg-emerald-600 text-white px-3 py-1.5 text-[10px] sm:text-xs font-bold uppercase tracking-[0.2em] mb-4 animate-pulse-slow">
                <Sparkle size={12} weight="fill" /> Appena arrivato · Disponibile ora
              </div>
              <h2 className="text-2xl sm:text-4xl font-black tracking-tight mb-3 max-w-3xl mx-auto leading-tight">
                Gestione Pulizie.<br />
                <span className="text-emerald-600">Coordina lo staff senza chiamarli.</span>
              </h2>
              <p className="text-sm sm:text-base text-zinc-600 max-w-2xl mx-auto leading-relaxed">
                Genera un <strong>link pubblico tokenizzato</strong> e mandalo allo staff via WhatsApp. Loro vedono i turni di check-out dal telefono, marcano "Fatto" e tu vedi la stanza diventare verde <strong>in tempo reale</strong>. Niente più chiamate, niente più dubbi.
              </p>
            </div>
          </FadeIn>

          <FadeIn>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 sm:gap-4 mb-8">
              {[
                { icon: WhatsappLogo, title: 'Link da inviare in WhatsApp', desc: 'Lo staff non scarica app. Apre il link, vede i turni, finisce, preme Fatto. Tu sai tutto.', color: 'from-emerald-500 to-teal-700' },
                { icon: Camera, title: 'Foto sporcizia / danni / furto', desc: 'Lo staff scatta foto direttamente dal telefono. Compresse a <250KB in automatico. Tu ricevi alert in app.', color: 'from-teal-500 to-cyan-700' },
                { icon: ChartBar, title: 'Realtime · senza refresh', desc: 'SSE WebSocket integrato: la stanza si illumina verde all\'istante quando lo staff marca completato.', color: 'from-cyan-500 to-emerald-700' },
              ].map((f, i) => (
                <div key={i} className="bg-white border border-zinc-200 p-5 sm:p-6 hover:border-emerald-400 hover:shadow-md transition-all">
                  <div className={`w-12 h-12 bg-gradient-to-br ${f.color} flex items-center justify-center mb-3`}>
                    <f.icon size={22} weight="bold" className="text-white" />
                  </div>
                  <p className="text-sm font-black text-zinc-900 mb-1.5 tracking-tight leading-snug">{f.title}</p>
                  <p className="text-xs text-zinc-500 leading-relaxed">{f.desc}</p>
                </div>
              ))}
            </div>
          </FadeIn>

          <FadeIn>
            <div className="bg-white border border-zinc-200 p-5 sm:p-7 mb-8">
              <p className="text-[11px] font-black uppercase tracking-wider text-emerald-700 mb-4">Come funziona in 4 step</p>
              <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
                {[
                  { n: '1', t: 'Configura la struttura', d: 'Scegli "Appartamento" o "Stanze". Per le stanze definisci nomi e capienza max.' },
                  { n: '2', t: 'Importa o aggiungi turni', d: 'iCal Airbnb/Booking automatico oppure inserimento manuale (date + ospiti + note).' },
                  { n: '3', t: 'Genera il link staff', d: 'Master (tutte le strutture) o per-struttura. Lo condividi in WhatsApp con un click.' },
                  { n: '4', t: 'Lo staff usa il link', d: 'Vede i turni, segnala con foto se serve, preme Fatto. Tu vedi tutto in tempo reale.' },
                ].map((s, i) => (
                  <div key={i} className="flex flex-col">
                    <div className="w-8 h-8 bg-emerald-600 text-white flex items-center justify-center text-sm font-black tabular-nums mb-2">{s.n}</div>
                    <p className="text-xs font-black text-zinc-900 mb-1 leading-tight">{s.t}</p>
                    <p className="text-[11px] text-zinc-500 leading-relaxed">{s.d}</p>
                  </div>
                ))}
              </div>
            </div>
          </FadeIn>

          <FadeIn>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3 mb-8">
              {[
                { icon: Bed, text: 'Multi-stanza · capienza per stanza' },
                { icon: ArrowsClockwise, text: 'Sync iCal auto · idempotente' },
                { icon: ShieldCheck, text: 'Token rigenerabile · scope limitato' },
                { icon: ChartBar, text: 'Stats: KPI giornaliere e segnalazioni' },
              ].map((item, i) => (
                <div key={i} className="bg-white border border-zinc-200 p-3 sm:p-4 flex items-start gap-2 sm:gap-3">
                  <item.icon size={16} weight="bold" className="text-emerald-600 flex-shrink-0 mt-0.5" />
                  <p className="text-[11px] sm:text-xs text-zinc-700 leading-relaxed">{item.text}</p>
                </div>
              ))}
            </div>
          </FadeIn>

          <FadeIn>
            <div className="text-center">
              <button onClick={() => navigate('/register')} className="inline-flex items-center gap-3 bg-emerald-600 text-white font-bold uppercase tracking-wider text-sm px-10 py-4 hover:bg-emerald-700 transition-all hover:gap-4" data-testid="landing-pulizie-cta">
                <Sparkle size={14} weight="fill" /> Prova Gestione Pulizie · Gratis
              </button>
              <p className="text-[11px] text-zinc-500 mt-3">Incluso nel piano Premium · setup in 2 minuti</p>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* ═══════════════ CHECK-IN DIGITALE · PLUGIN A PREZZO PAZZESCO ═══════════════ */}
      <section className="py-12 sm:py-24 px-4 sm:px-6 bg-gradient-to-br from-violet-50 via-white to-fuchsia-50 relative overflow-hidden" id="coming-soon">
        <div className="absolute -top-20 -right-20 w-96 h-96 bg-violet-200 rounded-full blur-3xl opacity-30 pointer-events-none" />
        <div className="absolute -bottom-20 -left-20 w-96 h-96 bg-fuchsia-200 rounded-full blur-3xl opacity-30 pointer-events-none" />
        <div className="max-w-5xl mx-auto relative">
          <FadeIn>
            <div className="text-center mb-10">
              <div className="inline-flex items-center gap-2 bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white px-3 py-1.5 text-[10px] sm:text-xs font-bold uppercase tracking-[0.2em] mb-4">
                <Sparkle size={12} weight="fill" /> In arrivo · Plugin Add-on
              </div>
              <h2 className="text-2xl sm:text-4xl font-black tracking-tight mb-3 max-w-3xl mx-auto leading-tight">
                Check-in digitale + Schedine Polizia + ROSS 1000.<br />
                <span className="bg-gradient-to-r from-violet-700 to-fuchsia-700 bg-clip-text text-transparent">Tutto in un solo plugin.</span>
              </h2>
              <p className="text-sm sm:text-base text-zinc-600 max-w-2xl mx-auto leading-relaxed">
                Un singolo plugin che <strong>scansiona il documento</strong> dell'ospite via MRZ, <strong>invia le schedine alla Polizia di Stato</strong> e <strong>la statistica ROSS 1000 all'ISTAT</strong>. Niente più triple compilazioni.
              </p>
            </div>
          </FadeIn>

          <FadeIn>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-4 mb-8">
              {[
                {
                  icon: IdentificationCard,
                  title: 'Check-in digitale ospiti',
                  desc: 'Link multilingua via WhatsApp. L\'ospite scansiona il documento col telefono. GDPR-first: foto mai uploadate, OCR client-side.',
                  features: ['5 lingue (IT EN FR ES DE)', 'MRZ client-side · CIE/PSP', 'Capofamiglia + familiari'],
                  color: 'from-violet-600 to-fuchsia-700',
                  textColor: 'text-violet-700',
                },
                {
                  icon: ShieldCheck,
                  title: 'Schedine Alloggiati Web',
                  desc: 'Invio automatico SOAP/WSKEY al portale della Polizia di Stato entro 24h. Ricevuta PDF archiviata. Conformità Art. 109 TULPS.',
                  features: ['Auto-trasmissione 24h', 'Ricevuta PDF · backup ZIP', 'Auto-purge dati post-invio'],
                  color: 'from-indigo-600 to-violet-700',
                  textColor: 'text-indigo-700',
                },
                {
                  icon: ChartLine,
                  title: 'Statistica ROSS 1000 ISTAT',
                  desc: 'Compila e invia automaticamente la rilevazione ROSS 1000 ISTAT. Stop alle dimenticanze mensili. Conformità DPR 322/1989.',
                  features: ['Auto-compilazione mensile', 'Invio ISTAT entro 5° giorno', 'Report annuale incluso'],
                  color: 'from-fuchsia-600 to-pink-700',
                  textColor: 'text-fuchsia-700',
                },
              ].map((p, i) => (
                <div key={i} className="bg-white border border-zinc-200 hover:border-violet-400 transition-all p-5 sm:p-6 relative overflow-hidden">
                  <div className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r ${p.color}`} />
                  <div className={`w-11 h-11 bg-gradient-to-br ${p.color} flex items-center justify-center mb-3`}>
                    <p.icon size={20} weight="bold" className="text-white" />
                  </div>
                  <h3 className={`text-base font-black tracking-tight mb-2 ${p.textColor} leading-snug`}>{p.title}</h3>
                  <p className="text-xs text-zinc-600 leading-relaxed mb-3">{p.desc}</p>
                  <ul className="space-y-1 text-[11px] text-zinc-700">
                    {p.features.map((f, j) => (
                      <li key={j} className="flex items-start gap-1.5">
                        <Check size={11} weight="bold" className={`${p.textColor} flex-shrink-0 mt-0.5`} />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </FadeIn>

          {/* Plugin features banner — senza prezzo (ancora da definire) */}
          <FadeIn>
            <div className="bg-gradient-to-br from-violet-700 via-fuchsia-700 to-pink-700 text-white p-6 sm:p-10 relative overflow-hidden mb-8">
              <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl pointer-events-none" />
              <div className="relative grid grid-cols-1 sm:grid-cols-2 gap-6 items-center">
                <div>
                  <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur px-2.5 py-1 text-[10px] font-black uppercase tracking-wider mb-3">
                    <Fire size={11} weight="fill" className="text-amber-300" /> PLUGIN aggiuntivo · Lifetime
                  </div>
                  <p className="text-3xl sm:text-5xl font-black tracking-tight mb-3 leading-tight">
                    Tre integrazioni.<br />
                    <span className="text-amber-200">Un solo plugin.</span>
                  </p>
                  <p className="text-sm sm:text-base text-white/85 mb-2 leading-relaxed">
                    Stiamo definendo il prezzo finale. Sarà <strong className="text-amber-200">una tantum</strong>, lifetime, per tutti i tuoi alloggi.
                  </p>
                  <p className="text-xs text-white/70 leading-relaxed">
                    Iscriviti per essere tra i primi a saperlo — i primi avranno uno sconto early bird dedicato.
                  </p>
                </div>
                <div className="space-y-2">
                  {[
                    'Check-in digitale ospiti · multilingua',
                    'Invio schedine alla Polizia · automatico',
                    'Statistica ROSS 1000 · ISTAT automatica',
                    'Compatibile WSKEY già configurate',
                    'Auto-purge GDPR · solo log conformità',
                    'Setup in 5 minuti · zero configurazione',
                  ].map((b, i) => (
                    <div key={i} className="flex items-start gap-2 bg-white/10 backdrop-blur px-3 py-2">
                      <Check size={13} weight="bold" className="text-amber-300 flex-shrink-0 mt-0.5" />
                      <span className="text-xs font-medium">{b}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </FadeIn>

          <FadeIn>
            <div className="text-center">
              <button onClick={() => navigate('/register')} className="inline-flex items-center gap-3 bg-zinc-900 text-white font-bold uppercase tracking-wider text-sm px-10 py-4 hover:bg-zinc-800 transition-all hover:gap-4" data-testid="landing-coming-soon-cta">
                <Sparkle size={14} weight="fill" /> Iscriviti · ti avvisiamo al lancio
              </button>
              <p className="text-[11px] text-zinc-400 mt-3">Già iscritti riceveranno la notifica per primi · sconto early bird previsto</p>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* ═══════════════ QR CODE PLUGIN · €2.99 ═══════════════ */}
      <section className="py-12 sm:py-24 px-4 sm:px-6 bg-white" id="plugin-qrcode">
        <div className="max-w-5xl mx-auto">
          <FadeIn>
            <div className="text-center mb-10">
              <div className="inline-flex items-center gap-2 bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white px-3 py-1.5 text-[10px] sm:text-xs font-bold uppercase tracking-[0.2em] mb-4">
                <Sparkle size={12} weight="fill" /> Disponibile ora · PLUGIN
              </div>
              <h2 className="text-2xl sm:text-4xl font-black tracking-tight mb-3 max-w-3xl mx-auto leading-tight">
                QR Code Generator.<br />
                <span className="bg-gradient-to-r from-violet-700 to-fuchsia-700 bg-clip-text text-transparent">Statici, WiFi e Dinamici.</span>
              </h2>
              <p className="text-sm sm:text-base text-zinc-600 max-w-2xl mx-auto leading-relaxed">
                Crea QR Code <strong>illimitati</strong> in alta risoluzione 1200px. Per link diretti, reti WiFi, e — la killer feature — <strong>QR dinamici editabili dopo la stampa</strong>.
              </p>
            </div>
          </FadeIn>
          <FadeIn>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-4 mb-8">
              {[
                { icon: QrCode, title: 'QR Link Statico', desc: 'Genera un QR Code 1200px da qualsiasi URL. Menu, biglietti, locandine.', color: 'from-blue-600 to-violet-700' },
                { icon: Wifi, title: 'QR WiFi · Auto-connect', desc: 'L\'ospite scansiona e si connette. WPA, WEP, reti aperte.', color: 'from-violet-600 to-fuchsia-700' },
                { icon: Lightning, title: 'QR DINAMICO · Killer', desc: 'Stampi una volta, cambi il link quando vuoi. La stampa funziona per sempre.', color: 'from-fuchsia-600 to-pink-700', highlight: true },
              ].map((f, i) => (
                <div key={i} className={`bg-white border-2 ${f.highlight ? 'border-fuchsia-400 shadow-lg' : 'border-zinc-200'} hover:border-violet-400 transition-all p-5 sm:p-6 relative overflow-hidden`}>
                  <div className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r ${f.color}`} />
                  {f.highlight && <div className="absolute top-3 right-3 inline-flex items-center px-1.5 py-0.5 text-[9px] font-black uppercase tracking-wider bg-fuchsia-100 text-fuchsia-700">🔥 Killer</div>}
                  <div className={`w-11 h-11 bg-gradient-to-br ${f.color} flex items-center justify-center mb-3`}>
                    <f.icon size={20} weight="bold" className="text-white" />
                  </div>
                  <h3 className="text-base font-black tracking-tight mb-2 text-zinc-900 leading-snug">{f.title}</h3>
                  <p className="text-xs text-zinc-600 leading-relaxed">{f.desc}</p>
                </div>
              ))}
            </div>
          </FadeIn>
          <FadeIn>
            <div className="bg-zinc-50 border border-zinc-200 p-5 sm:p-7 mb-8">
              <p className="text-[11px] font-black uppercase tracking-wider text-zinc-600 mb-4">Statico vs Dinamico · qual è la differenza?</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="bg-white border border-zinc-200 p-4">
                  <p className="text-sm font-black text-zinc-900 mb-2">📌 QR Statico</p>
                  <p className="text-xs text-zinc-600 leading-relaxed mb-3">Il QR contiene <strong>direttamente</strong> il link. Una volta stampato, il link non si può più cambiare.</p>
                  <p className="text-[11px] text-zinc-400">Ideale per: menu, biglietti, eventi singoli</p>
                </div>
                <div className="bg-gradient-to-br from-violet-50 to-fuchsia-50 border-2 border-fuchsia-300 p-4">
                  <p className="text-sm font-black text-fuchsia-700 mb-2">⚡ QR Dinamico</p>
                  <p className="text-xs text-zinc-700 leading-relaxed mb-3">Il QR punta a un short-link che redirige al link corrente. <strong>Cambi il link in 2 secondi</strong>, la stampa funziona per sempre.</p>
                  <p className="text-[11px] text-fuchsia-600 font-bold">✓ Targhe, locandine, prodotti fisici, marketing</p>
                </div>
              </div>
            </div>
          </FadeIn>
          <FadeIn>
            <div className="bg-gradient-to-br from-violet-700 via-fuchsia-700 to-pink-700 text-white p-6 sm:p-10 relative overflow-hidden mb-8">
              <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl pointer-events-none" />
              <div className="relative grid grid-cols-1 sm:grid-cols-2 gap-6 items-center">
                <div>
                  <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur px-2.5 py-1 text-[10px] font-black uppercase tracking-wider mb-3">
                    <Fire size={11} weight="fill" className="text-amber-300" /> Lifetime · QR illimitati
                  </div>
                  <div className="flex items-baseline gap-2 mb-3">
                    <span className="text-5xl sm:text-6xl font-black tracking-tighter">€ 2,99</span>
                    <span className="text-sm text-white/70 uppercase tracking-wider font-bold">una tantum</span>
                  </div>
                  <p className="text-sm font-bold text-amber-200 mb-2">Paghi una volta. QR illimitati. Per sempre.</p>
                  <p className="text-xs text-white/70 leading-relaxed mb-4">Statici, WiFi e dinamici. Download PNG 600px. Nessun watermark.</p>
                  <button onClick={() => navigate('/register')} className="inline-flex items-center gap-2 bg-white text-violet-700 font-black uppercase tracking-wider text-xs px-6 py-3 hover:bg-violet-50 transition-all" data-testid="landing-qrcode-cta">
                    <Sparkle size={12} weight="fill" /> Inizia ora
                  </button>
                </div>
                <div className="space-y-2">
                  {['QR Statico · alta risoluzione 1200px','QR WiFi (WPA/WEP/Aperta) · auto-connect','QR Dinamico · editabile dopo la stampa','Download PNG illimitati · qualità stampa','Tracking scansioni · counter per QR','Nessun watermark · nessuna scadenza'].map((b, i) => (
                    <div key={i} className="flex items-start gap-2 bg-white/10 backdrop-blur px-3 py-2">
                      <Check size={13} weight="bold" className="text-amber-300 flex-shrink-0 mt-0.5" />
                      <span className="text-xs font-medium">{b}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* ═══════════════ CALENDARIO SCADENZE ═══════════════ */}
      <section className="py-12 sm:py-24 px-4 sm:px-6 bg-gradient-to-b from-indigo-50 to-blue-50" id="scadenze">
        <div className="max-w-5xl mx-auto">
          <FadeIn>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-8 h-8 bg-blue-600 flex items-center justify-center">
                <Calendar size={16} weight="bold" className="text-white" />
              </div>
              <span className="text-xs font-bold uppercase tracking-widest text-blue-600">Funzionalità #5 · Novità</span>
            </div>
            <h2 className="text-xl sm:text-3xl font-black tracking-tight mb-2">Calendario scadenze fiscali e amministrative</h2>
            <p className="text-sm text-zinc-500 max-w-2xl mb-8 leading-relaxed">
              Mai più una scadenza dimenticata. ROSS1000, imposta di soggiorno, controllo estintore e dichiarazione annuale all'Agenzia delle Entrate. Promemoria automatici via email e alert visivi in dashboard.
            </p>
          </FadeIn>

          <FadeIn>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-8">
              {[
                {
                  icon: Receipt,
                  title: 'Imposta di soggiorno',
                  sub: 'Mensile o trimestrale, a tua scelta. Alert 3 / 7 giorni prima della scadenza',
                  color: 'bg-blue-100 text-blue-700',
                },
                {
                  icon: ChartLine,
                  title: 'ROSS1000',
                  sub: 'Rilevazione mensile Banca d\'Italia / ISTAT. Promemoria 5 giorni prima fine mese',
                  color: 'bg-indigo-100 text-indigo-700',
                },
                {
                  icon: Fire,
                  title: 'Controllo estintore',
                  sub: 'Calcolato in automatico a 6 mesi dall\'ultimo controllo. Alert 7 giorni prima',
                  color: 'bg-orange-100 text-orange-700',
                },
                {
                  icon: FileText,
                  title: 'Dichiarazione annuale',
                  sub: 'Imposta soggiorno → Agenzia Entrate entro il 30 giugno. Alert dal 15 giugno',
                  color: 'bg-violet-100 text-violet-700',
                },
              ].map((item, i) => (
                <div key={i} className="bg-white border border-zinc-200 p-4 sm:p-5 hover:shadow-md hover:-translate-y-0.5 transition-all">
                  <div className={`w-10 h-10 ${item.color} flex items-center justify-center mb-3 rounded-lg`}>
                    <item.icon size={20} weight="bold" />
                  </div>
                  <p className="text-sm font-black text-zinc-900 mb-1 tracking-tight">{item.title}</p>
                  <p className="text-[11px] text-zinc-500 leading-relaxed">{item.sub}</p>
                </div>
              ))}
            </div>
          </FadeIn>

          <FadeIn>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
              <div className="bg-white border border-zinc-200 p-5 sm:p-6">
                <h4 className="font-bold text-sm text-zinc-900 mb-3">Come funziona</h4>
                <div className="space-y-2">
                  {[
                    { step: '1', text: 'Indichi la frequenza della tua imposta di soggiorno (mensile / trimestrale)' },
                    { step: '2', text: 'Inserisci la data dell\'ultimo controllo estintore: il sistema calcola il prossimo' },
                    { step: '3', text: 'ROSS1000 e dichiarazione annuale sono pre-impostate sul calendario fiscale' },
                    { step: '4', text: 'In dashboard vedi sempre cosa scade nei prossimi giorni con badge colorati' },
                    { step: '5', text: 'Ricevi una email solo nel giorno giusto, niente spam' },
                  ].map((s, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <span className="w-6 h-6 bg-blue-100 text-blue-700 flex items-center justify-center text-[11px] font-black flex-shrink-0">{s.step}</span>
                      <p className="text-xs text-zinc-600 leading-relaxed">{s.text}</p>
                    </div>
                  ))}
                </div>
              </div>
              <div className="bg-blue-900 text-white p-5 sm:p-6 relative overflow-hidden">
                <div className="absolute -right-12 -top-12 w-48 h-48 rounded-full bg-blue-500/20 blur-3xl pointer-events-none" />
                <h4 className="font-bold text-sm mb-3 relative">Trigger promemoria</h4>
                <div className="space-y-1.5 font-mono text-[10px] sm:text-[11px] text-blue-200 relative">
                  <div className="flex gap-2"><span className="text-blue-400">Imposta mens.:</span><span>3 giorni prima fine mese</span></div>
                  <div className="flex gap-2"><span className="text-blue-400">Imposta trim.:</span><span>7 giorni prima fine Q (31/3 - 30/6 - 30/9 - 31/12)</span></div>
                  <div className="flex gap-2"><span className="text-blue-400">ROSS1000:</span><span>5 giorni prima fine mese</span></div>
                  <div className="flex gap-2"><span className="text-blue-400">Estintore:</span><span>7 giorni prima ultima_data + 6 mesi</span></div>
                  <div className="flex gap-2"><span className="text-blue-400">Dich. annuale:</span><span>15 giugno (scadenza 30/6)</span></div>
                  <div className="flex gap-2"><span className="text-blue-400">Email:</span><span>Brevo · 08:00 UTC · idempotente</span></div>
                </div>
                <div className="mt-4 pt-3 border-t border-blue-700 relative">
                  <p className="text-[10px] text-blue-300 flex items-center gap-1.5">
                    <Bell size={11} weight="fill" className="text-blue-400" />
                    Tutto attivabile o disattivabile dal pannello impostazioni
                  </p>
                </div>
              </div>
            </div>
          </FadeIn>

          <FadeIn>
            <div className="text-center">
              <button onClick={() => navigate('/register')} className="inline-flex items-center gap-3 bg-blue-600 text-white font-bold uppercase tracking-wider text-sm px-10 py-4 hover:bg-blue-700 transition-all hover:gap-4" data-testid="landing-deadlines-cta">
                Attiva i promemoria <ArrowRight size={20} weight="bold" />
              </button>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* ═══════════════ PRICING ═══════════════ */}
      <section className="py-16 sm:py-24 bg-zinc-50 px-4 sm:px-6" id="prezzi">
        <div className="max-w-3xl mx-auto">
          <FadeIn>
            <h2 className="text-center text-xl sm:text-2xl font-black tracking-tight mb-2">Prezzi semplici, trasparenti</h2>
            <p className="text-center text-zinc-500 text-sm mb-10">Contratti + Ricevute + Contabilità. Tutto incluso.</p>
          </FadeIn>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FadeIn>
              <div className="bg-white border border-zinc-200 p-6 hover:shadow-lg transition-all h-full">
                <p className="text-xs font-bold uppercase tracking-wider text-zinc-400 mb-3">Mensile</p>
                <div className="flex items-baseline gap-1 mb-1"><span className="text-3xl font-black">€6,99</span><span className="text-zinc-400 text-sm">/mese</span></div>
                <p className="text-xs text-zinc-500 mb-5">Annullabile in qualsiasi momento.</p>
                <ul className="space-y-2 mb-6">{['Contratti illimitati','Ricevute illimitate','Contabilità completa','5 lingue','Firma FES','Grafici e report'].map((f,i)=>(<li key={i} className="flex items-center gap-2 text-xs text-zinc-700"><Check size={14} weight="bold" className="text-teal-600 flex-shrink-0"/>{f}</li>))}</ul>
                <button onClick={() => navigate('/register')} className="w-full py-3 text-xs font-bold uppercase tracking-wider border border-zinc-900 text-zinc-900 hover:bg-zinc-900 hover:text-white transition-colors" data-testid="pricing-monthly-btn">Inizia gratis</button>
              </div>
            </FadeIn>
            <FadeIn delay={100}>
              <div className="bg-zinc-900 text-white p-6 relative overflow-hidden hover:shadow-xl transition-all h-full">
                <div className="absolute top-3 right-3 bg-emerald-400 text-zinc-900 text-[10px] font-bold uppercase tracking-wider px-2 py-1">-40%</div>
                <p className="text-xs font-bold uppercase tracking-wider text-zinc-400 mb-3">Annuale</p>
                <div className="flex items-baseline gap-1 mb-1"><span className="text-3xl font-black">€49,99</span><span className="text-zinc-400 text-sm">/anno</span></div>
                <p className="text-xs text-zinc-400 mb-5">Solo €3,33/mese.</p>
                <ul className="space-y-2 mb-4">{['Contratti illimitati','Ricevute illimitate','Contabilità completa','5 lingue','Firma FES','Grafici e report','Risparmio 40%'].map((f,i)=>(<li key={i} className="flex items-center gap-2 text-xs text-zinc-300"><Check size={14} weight="bold" className="text-emerald-400 flex-shrink-0"/>{f}</li>))}</ul>
                <div className="bg-gradient-to-br from-fuchsia-600/20 to-indigo-600/20 border border-fuchsia-500/40 p-3 mb-5 relative">
                  <div className="absolute -top-2 left-3 bg-yellow-400 text-zinc-900 text-[9px] font-black uppercase tracking-wider px-2 py-0.5">Bonus incluso</div>
                  <div className="flex items-start gap-2 mt-1">
                    <Gift size={18} weight="fill" className="text-yellow-300 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-xs font-black text-white leading-tight mb-1">1 ora gratis con Sergio</p>
                      <p className="text-[11px] text-fuchsia-100 leading-snug">Call Google Meet per imparare tutto su Locazione Turistica · Valore €40</p>
                    </div>
                  </div>
                </div>
                <button onClick={() => navigate('/register')} className="w-full py-3 text-xs font-bold uppercase tracking-wider bg-white text-zinc-900 hover:bg-zinc-100 transition-colors" data-testid="pricing-yearly-btn">Inizia gratis</button>
              </div>
            </FadeIn>
          </div>
          <p className="text-center text-[11px] text-zinc-400 mt-5">7 giorni di prova gratuita. Nessun obbligo. Annulla quando vuoi.</p>
        </div>
      </section>

      {/* ═══════════════ FAQ ═══════════════ */}
      <section className="py-16 sm:py-24 px-4 sm:px-6" id="faq" data-testid="faq-section">
        <div className="max-w-3xl mx-auto">
          <FadeIn>
            <p className="text-center text-xs font-bold uppercase tracking-widest text-zinc-400 mb-2">Domande frequenti</p>
            <h2 className="text-center text-xl sm:text-2xl font-black tracking-tight mb-8">Tutto su contratti, ricevute e contabilità</h2>
          </FadeIn>
          <Accordion type="single" collapsible className="w-full">
            {faqData.map((item, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="border-b border-zinc-200">
                <AccordionTrigger className="py-4 text-left text-sm font-bold text-zinc-900 hover:no-underline hover:text-zinc-700" data-testid={`faq-trigger-${i}`}>{item.q}</AccordionTrigger>
                <AccordionContent className="text-xs text-zinc-600 leading-relaxed pb-4">{item.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* ═══════════════ CTA ═══════════════ */}
      <section className="py-14 sm:py-20 bg-zinc-900 text-white px-4 sm:px-6">
        <div className="max-w-3xl mx-auto text-center">
          <FadeIn>
            <h2 className="text-xl sm:text-3xl font-black tracking-tight mb-4">Pronto a gestire la tua locazione turistica?</h2>
            <p className="text-zinc-400 mb-8 text-sm">Contratti, ricevute e contabilità. Tutto in un unico posto.</p>
            <button onClick={() => navigate('/register')} className="glow-btn inline-flex items-center gap-3 bg-teal-500 text-white font-bold uppercase tracking-wider text-sm px-10 py-5 hover:bg-teal-600 transition-all hover:gap-4" data-testid="landing-cta-bottom-btn">Inizia gratis — 7 giorni di prova <ArrowRight size={20} weight="bold" /></button>
          </FadeIn>
        </div>
      </section>

      {/* ═══════════════ FOOTER ═══════════════ */}
      <footer className="bg-zinc-50 border-t border-zinc-200 pt-10 sm:pt-14 pb-8 px-4 sm:px-6" data-testid="landing-footer">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 mb-10">
            <div>
              <Logo size="xs" className="mb-3" />
              <p className="text-xs text-zinc-500 leading-relaxed mb-3">Piattaforma all-in-one per host: contratti locazione turistica, ricevute affitto breve, contabilità con commissioni Airbnb e Booking.</p>
              <div className="flex items-center gap-2 text-xs text-zinc-400"><IdentificationCard size={14} /><span>P.IVA 02903680995</span></div>
            </div>
            <div>
              <h4 className="text-xs font-bold uppercase tracking-widest text-zinc-900 mb-3">Piattaforma</h4>
              <ul className="space-y-2">
                <li><Link to="/register" className="text-xs text-zinc-500 hover:text-zinc-900 transition-colors" data-testid="footer-register-link">Registrati gratis</Link></li>
                <li><Link to="/login" className="text-xs text-zinc-500 hover:text-zinc-900 transition-colors" data-testid="footer-login-link">Accedi</Link></li>
                <li><a href="#prezzi" className="text-xs text-zinc-500 hover:text-zinc-900 transition-colors" data-testid="footer-pricing-link">Prezzi</a></li>
                <li><a href="#faq" className="text-xs text-zinc-500 hover:text-zinc-900 transition-colors" data-testid="footer-faq-link">FAQ</a></li>
                <li><Link to="/guide" className="text-xs text-zinc-500 hover:text-zinc-900 transition-colors" data-testid="footer-guide-link">Guide host</Link></li>
                <li><Link to="/contact" className="text-xs text-zinc-500 hover:text-zinc-900 transition-colors" data-testid="footer-contact-link">Contattaci</Link></li>
                <li><Link to="/stats" className="text-xs text-zinc-500 hover:text-zinc-900 transition-colors" data-testid="footer-stats-link">Host in numeri</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-xs font-bold uppercase tracking-widest text-zinc-900 mb-3">Per chi</h4>
              <ul className="space-y-2">
                <li><Link to="/host-airbnb" className="text-xs text-zinc-500 hover:text-zinc-900 transition-colors" data-testid="footer-link-host-airbnb">Host Airbnb</Link></li>
                <li><Link to="/host-booking" className="text-xs text-zinc-500 hover:text-zinc-900 transition-colors" data-testid="footer-link-host-booking">Host Booking.com</Link></li>
                <li><Link to="/gestione-bnb" className="text-xs text-zinc-500 hover:text-zinc-900 transition-colors" data-testid="footer-link-gestione-bnb">Gestione B&amp;B</Link></li>
                <li><Link to="/gestione-casa-vacanza" className="text-xs text-zinc-500 hover:text-zinc-900 transition-colors" data-testid="footer-link-gestione-cav">Case vacanze (CAV)</Link></li>
              </ul>
              <h4 className="text-xs font-bold uppercase tracking-widest text-zinc-900 mb-3 mt-5">Strumenti</h4>
              <ul className="space-y-2">
                <li><Link to="/contratto-locazione-turistica" className="text-xs text-zinc-500 hover:text-zinc-900 transition-colors" data-testid="footer-link-contratto">Contratto locazione turistica</Link></li>
                <li><Link to="/ricevuta-affitto-breve" className="text-xs text-zinc-500 hover:text-zinc-900 transition-colors" data-testid="footer-link-ricevuta">Ricevuta affitto breve</Link></li>
                <li><Link to="/schedine-alloggiati-web" className="text-xs text-zinc-500 hover:text-zinc-900 transition-colors" data-testid="footer-link-schedine">Schedine Alloggiati Web</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-xs font-bold uppercase tracking-widest text-zinc-900 mb-3">Legale</h4>
              <ul className="space-y-2 mb-4">
                <li><Link to="/terms" className="text-xs text-zinc-500 hover:text-zinc-900 transition-colors" data-testid="footer-terms-link">Termini e Condizioni</Link></li>
                <li><Link to="/privacy" className="text-xs text-zinc-500 hover:text-zinc-900 transition-colors" data-testid="footer-privacy-link">Privacy Policy</Link></li>
                <li><Link to="/cookie" className="text-xs text-zinc-500 hover:text-zinc-900 transition-colors" data-testid="footer-cookie-link">Cookie Policy</Link></li>
                <li>
                  <button
                    type="button"
                    onClick={() => window.dispatchEvent(new CustomEvent('open-cookie-preferences'))}
                    className="text-xs text-zinc-500 hover:text-zinc-900 transition-colors underline-offset-2 hover:underline"
                    data-testid="footer-cookie-prefs-link"
                  >
                    Gestisci preferenze cookie
                  </button>
                </li>
              </ul>
              <h4 className="text-xs font-bold uppercase tracking-widest text-zinc-900 mb-2">Contatti</h4>
              <div className="space-y-1.5">
                <div className="flex items-center gap-2 text-xs text-zinc-500"><Envelope size={12} className="flex-shrink-0" /><a href="mailto:vitadahost@gmail.com" className="hover:text-zinc-900 transition-colors">vitadahost@gmail.com</a></div>
                <div className="flex items-center gap-2 text-xs text-zinc-500"><MapPin size={12} className="flex-shrink-0" /><span>Genova, Italia</span></div>
              </div>
            </div>
          </div>
          <div className="border-t border-zinc-200 pt-5 flex flex-col sm:flex-row items-center justify-between gap-2">
            <p className="text-[11px] text-zinc-400">&copy; {new Date().getFullYear()} Locazione Turistica — Sergio Battelli — Vita da Host — Genova</p>
            <p className="text-[11px] text-zinc-400">Contratti affitti brevi · Ricevute affitto breve · Contabilità host · Commissioni Airbnb Booking · Firma FES eIDAS</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Plugin dropdown (desktop nav)
// ─────────────────────────────────────────────────────────────
function PluginDropdown({ navigate }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const items = [
    {
      key: 'qrcode',
      label: 'QR Code Generator',
      desc: 'Statici, WiFi, dinamici editabili',
      hash: '#plugin-qrcode',
      badge: 'NEW',
      color: 'text-violet-700',
    },
    {
      key: 'pulizie',
      label: 'Gestione Pulizie',
      desc: 'Coordina staff in realtime',
      hash: '#pulizie',
      badge: 'NEW',
      color: 'text-emerald-700',
    },
    {
      key: 'pulizie-app',
      label: 'WebApp Pulizie Host',
      desc: 'Spin-off mobile · realtime',
      hash: '#pulizie',
      badge: 'NEW',
      color: 'text-emerald-700',
    },
    {
      key: 'checkin',
      label: 'Self Check-in ospiti',
      desc: 'OCR documenti + schedine',
      hash: '#coming-soon',
      badge: 'IN ARRIVO',
      color: 'text-zinc-500',
    },
  ];

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(v => !v)}
        className={`inline-flex items-center gap-1 uppercase tracking-wider hover:text-violet-700 transition-colors ${open ? 'text-violet-700' : ''}`}
        aria-expanded={open}
        aria-haspopup="true"
        data-testid="nav-plugin-dropdown"
      >
        Plugin
        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" className={`transition-transform ${open ? 'rotate-180' : ''}`}>
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-2 w-72 bg-white border border-zinc-200 shadow-xl z-[60] animate-in fade-in slide-in-from-top-1 duration-150" data-testid="plugin-dropdown-menu">
          <div className="px-4 py-3 border-b border-zinc-100 bg-gradient-to-r from-violet-50 to-fuchsia-50">
            <p className="text-[10px] font-black uppercase tracking-[0.18em] text-violet-700">PLUGIN & WebApp</p>
            <p className="text-[11px] text-zinc-500 mt-0.5">Estendi il tuo gestionale</p>
          </div>
          <div className="py-1">
            {items.map(item => (
              <button
                key={item.key}
                onClick={() => {
                  setOpen(false);
                  if (item.hash) {
                    const el = document.querySelector(item.hash);
                    if (el) {
                      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    } else {
                      window.location.hash = item.hash;
                    }
                  }
                }}
                className="w-full text-left px-4 py-2.5 hover:bg-zinc-50 transition-colors group"
                data-testid={`plugin-item-${item.key}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-bold ${item.color} group-hover:text-zinc-900 transition-colors normal-case tracking-normal`}>
                      {item.label}
                    </p>
                    <p className="text-[11px] text-zinc-400 normal-case tracking-normal mt-0.5">{item.desc}</p>
                  </div>
                  {item.badge && (
                    <span className={`text-[8px] font-black uppercase tracking-wider px-1.5 py-0.5 flex-shrink-0 ${item.badge === 'NEW' ? 'bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white' : 'bg-zinc-100 text-zinc-500'}`}>
                      {item.badge}
                    </span>
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

